import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';
import DragIndicatorRoundedIcon from '@mui/icons-material/DragIndicatorRounded';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import {
  createIndustrialCostPlantUserConfig,
  createIndustrialCostProductionLineConfig,
  createIndustrialCostProductionLineGroupConfig,
  createIndustrialCostProductionReportQualityControlConfig,
  createIndustrialCostProductionReportStopReasonConfig,
  createIndustrialCostProductionShiftConfig,
  deactivateIndustrialCostProductionLineConfig,
  deactivateIndustrialCostProductionLineGroupConfig,
  deactivateIndustrialCostProductionReportQualityControlConfig,
  deactivateIndustrialCostProductionReportStopReasonConfig,
  deactivateIndustrialCostProductionShiftConfig,
  getIndustrialCostProductionReportOperatorContext,
  listIndustrialCostProductionLineGroupsConfig,
  listIndustrialCostProductionLinesConfig,
  listIndustrialCostProductionReportQualityControlsConfig,
  listIndustrialCostProductionReportStopReasonsConfig,
  listIndustrialCostProductionShiftsConfig,
  resetIndustrialCostPlantUserPasswordConfig,
  updateIndustrialCostPlantUserConfig,
  updateIndustrialCostProductionLineConfig,
  updateIndustrialCostProductionLineGroupConfig,
  updateIndustrialCostProductionReportQualityControlConfig,
  updateIndustrialCostProductionReportStopReasonConfig,
  updateIndustrialCostProductionShiftConfig,
  getIndustrialCostProductionOperationSettingsConfig,
  updateIndustrialCostProductionOperationSettingsConfig,
  listIndustrialCostProductionMaterialLots,
  createIndustrialCostProductionMaterialLot,
  updateIndustrialCostProductionMaterialLot,
} from '../services/industrialCostService';
import {
  applyImportedManufacturingOrdersImportMapping,
  confirmImportedManufacturingOrdersImport,
  createImportedManufacturingOrdersImportFlowState,
  runImportedManufacturingOrdersImportPreview,
} from '../importFlows/importedManufacturingOrdersImportFlow';
import {
  analyzeFileNormalization,
  commitFileNormalization,
  reviewFileNormalization,
} from '../fileNormalization/service';
import type { FileNormalizationAnalyzeResult } from '../fileNormalization/types';
import type {
  CreatePlantUserPayload,
  PlantUserAccessRole,
  PlantUserPermissionsOverride,
  ResetPlantUserPasswordPayload,
  UpdatePlantUserPayload,
  ProductionLineConfig,
  ProductionLineGroup,
  ProductionReportQualityControl,
  ProductionReportStopReason,
  ProductionReportTemplate,
  ProductionShiftConfig,
  UpsertProductionLineConfigPayload,
  UpsertProductionLineGroupPayload,
  UpsertProductionReportQualityControlPayload,
  UpsertProductionReportStopReasonPayload,
  UpsertProductionShiftConfigPayload,
  ProductionOperationSettings,
  ProductionAdditionalFieldConfig,
  ProductionReportFormLayoutConfig,
  ImportedManufacturingOrderImportPreviewResponse,
  ImportedManufacturingOrderImportConfirmResponse,
  UpdateProductionOperationSettingsPayload,
  ProductionMaterialLot,
  ProductionMaterialLotStatus,
  ProductionMaterialLotQualityStatus,
  CreateProductionMaterialLotPayload,
} from '../types/industrialCost';
import { IMPORT_PERMISSION_DENIED_TEXT, useCanManageImports } from '../utils/importPermission';

type ConfigTab =
  | 'lines'
  | 'shifts'
  | 'stop-reasons'
  | 'quality-controls'
  | 'traceability'
  | 'operation';

type DialogMode = 'create' | 'edit';

type LineGroupForm = UpsertProductionLineGroupPayload & { id?: string };
type LineForm = UpsertProductionLineConfigPayload & { id?: string };
type ShiftForm = UpsertProductionShiftConfigPayload & { id?: string };
type StopReasonForm = UpsertProductionReportStopReasonPayload & { id?: string };
type QualityControlForm = UpsertProductionReportQualityControlPayload & { id?: string };
type PlantUserForm = {
  access_id?: string;
  email: string;
  password: string;
  full_name: string;
  role: PlantUserAccessRole;
  is_active: boolean;
  permissions_override: PlantUserPermissionsOverride;
};

type PlantUserPasswordForm = {
  access_id: string;
  email: string;
  new_password: string;
};

type MaterialLotForm = {
  id?: string;
  material_name: string;
  lot_number: string;
  supplier_name: string;
  supplier_lot_number: string;
  received_at: string;
  expiration_date: string;
  quantity_received: string;
  quantity_available: string;
  quantity_unit: string;
  status: ProductionMaterialLotStatus;
  quality_status: ProductionMaterialLotQualityStatus;
  notes: string;
};

const emptyLineGroupForm: LineGroupForm = {
  code: '',
  name: '',
  description: '',
  is_active: true,
  display_order: 0,
};

const emptyLineForm: LineForm = {
  code: '',
  name: '',
  production_line_group_id: null,
  process_group: '',
  description: '',
  is_active: true,
  display_order: 0,
};

const emptyShiftForm: ShiftForm = {
  code: '',
  label: '',
  start_time: '06:00',
  end_time: '14:00',
  is_active: true,
  display_order: 0,
};

const PRODUCTION_WORKING_WEEKDAYS = [
  { value: 0, label: 'Lunes' },
  { value: 1, label: 'Martes' },
  { value: 2, label: 'Miércoles' },
  { value: 3, label: 'Jueves' },
  { value: 4, label: 'Viernes' },
  { value: 5, label: 'Sábado' },
  { value: 6, label: 'Domingo' },
] as const;

const emptyStopReasonForm: StopReasonForm = {
  code: '',
  label: '',
  is_active: true,
  display_order: 0,
};

const emptyQualityControlForm: QualityControlForm = {
  template_id: '',
  code: '',
  label: '',
  description: '',
  is_active: true,
  display_order: 0,
};

const emptyPlantUserForm: PlantUserForm = {
  email: '',
  password: '',
  full_name: '',
  role: 'factory_operator',
  is_active: true,
  permissions_override: {},
};

const emptyPlantUserPasswordForm: PlantUserPasswordForm = {
  access_id: '',
  email: '',
  new_password: '',
};

const emptyMaterialLotForm: MaterialLotForm = {
  material_name: '',
  lot_number: '',
  supplier_name: '',
  supplier_lot_number: '',
  received_at: '',
  expiration_date: '',
  quantity_received: '',
  quantity_available: '',
  quantity_unit: 'kg',
  status: 'available',
  quality_status: 'not_required',
  notes: '',
};

const factoryManagerBasePermissions = new Set([
  'industrial:access',
  'factory_control:view',
  'production_history:view',
  'production_performance:view',
  'production_reports:view',
  'production_reports:operate',
  'production_reports:validate',
  'results:view',
]);

const configurablePlantUserPermissions = [
  {
    key: 'factory_control:view',
    label: 'Ver control de fábrica',
    group: 'Producción',
  },
  {
    key: 'production_history:view',
    label: 'Ver histórico',
    group: 'Producción',
  },
  {
    key: 'production_performance:view',
    label: 'Ver rendimiento',
    group: 'Producción',
  },
  {
    key: 'production_reports:view',
    label: 'Ver partes',
    group: 'Partes',
  },
  {
    key: 'production_reports:operate',
    label: 'Operar partes',
    group: 'Partes',
  },
  {
    key: 'production_reports:validate',
    label: 'Validar / reabrir partes',
    group: 'Partes',
  },
  {
    key: 'plant_config:view',
    label: 'Ver configuración de planta',
    group: 'Configuración',
  },
  {
    key: 'plant_config:manage',
    label: 'Modificar configuración de planta',
    group: 'Configuración',
  },
  {
    key: 'plant_users:manage',
    label: 'Gestionar usuarios',
    group: 'Usuarios',
  },
  {
    key: 'costs:view',
    label: 'Ver costes',
    group: 'Costes',
  },
  {
    key: 'costs:manage',
    label: 'Gestionar costes',
    group: 'Costes',
  },
  {
    key: 'results:view',
    label: 'Ver resultados',
    group: 'Resultados',
  },
  {
    key: 'imports:manage',
    label: 'Gestionar importaciones',
    group: 'Importaciones',
  },
];

function normalizePlantUserPermissionsOverride(
  override: PlantUserPermissionsOverride | null | undefined,
): PlantUserPermissionsOverride {
  const allowedKeys = new Set(configurablePlantUserPermissions.map((permission) => permission.key));

  const allow = Array.from(
    new Set((override?.allow ?? []).filter((permission) => allowedKeys.has(permission))),
  );

  const deny = Array.from(
    new Set((override?.deny ?? []).filter((permission) => allowedKeys.has(permission))),
  ).filter((permission) => !allow.includes(permission));

  return {
    allow,
    deny,
  };
}

function resolvePlantUserPermissionEnabled(
  role: PlantUserAccessRole,
  override: PlantUserPermissionsOverride | null | undefined,
  permission: string,
) {
  if (role === 'admin') return true;
  if (role === 'factory_operator') {
    return permission === 'production_reports:operate';
  }

  const normalized = normalizePlantUserPermissionsOverride(override);

  if (normalized.allow?.includes(permission)) return true;
  if (normalized.deny?.includes(permission)) return false;

  return factoryManagerBasePermissions.has(permission);
}

function buildNextPlantUserPermissionsOverride(
  role: PlantUserAccessRole,
  override: PlantUserPermissionsOverride | null | undefined,
  permission: string,
  enabled: boolean,
): PlantUserPermissionsOverride {
  if (role !== 'factory_manager') return {};

  const normalized = normalizePlantUserPermissionsOverride(override);
  const baseEnabled = factoryManagerBasePermissions.has(permission);

  const allow = new Set(normalized.allow ?? []);
  const deny = new Set(normalized.deny ?? []);

  allow.delete(permission);
  deny.delete(permission);

  if (enabled !== baseEnabled) {
    if (enabled) {
      allow.add(permission);
    } else {
      deny.add(permission);
    }
  }

  return {
    allow: Array.from(allow),
    deny: Array.from(deny),
  };
}

function getErrorMessage(error: unknown) {
  if (error instanceof Error) return error.message;
  return 'No se pudo completar la operación.';
}

function normalizeCode(value: string) {
  return value
    .trim()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .toUpperCase();
}

function formatPlanningDurationUnitLabel(value: string | null | undefined) {
  if (value === 'hours') return 'Horas';
  if (value === 'minutes') return 'Minutos';
  return 'Sin unidad';
}

type ProductionReportOrderFieldKey =
  | 'manufacturing_order_number'
  | 'product'
  | 'customer'
  | 'customer_order'
  | 'planned_quantity'
  | 'speed'
  | 'actual_quantity'
  | 'observations';

type ProductionReportFormLayoutSectionKey =
  ProductionReportFormLayoutConfig['sections'][number]['key'];

const productionReportFormLayoutSectionDefinitions: Array<{
  key: ProductionReportFormLayoutSectionKey;
  label: string;
  description: string;
  tone: string;
}> = [
  {
    key: 'identity',
    label: 'Identificación de la orden',
    description: 'Datos que identifican la OF, producto, cliente y referencias.',
    tone: '#38bdf8',
  },
  {
    key: 'production',
    label: 'Parámetros de fabricación',
    description: 'Datos operativos que alimentan el arranque y la estimación.',
    tone: '#22c55e',
  },
  {
    key: 'additional',
    label: 'Datos adicionales',
    description: 'Campos propios que complementan la orden sin afectar al núcleo.',
    tone: '#a78bfa',
  },
  {
    key: 'closing',
    label: 'Cierre de orden',
    description: 'Datos que normalmente se completan al finalizar la orden.',
    tone: '#fbbf24',
  },
];

const defaultProductionReportOrderFieldLayout: Array<{
  key: ProductionReportOrderFieldKey;
  sectionKey: ProductionReportFormLayoutSectionKey;
}> = [
  { key: 'manufacturing_order_number', sectionKey: 'identity' },
  { key: 'product', sectionKey: 'identity' },
  { key: 'customer', sectionKey: 'identity' },
  { key: 'customer_order', sectionKey: 'identity' },
  { key: 'planned_quantity', sectionKey: 'production' },
  { key: 'speed', sectionKey: 'production' },
  { key: 'actual_quantity', sectionKey: 'closing' },
  { key: 'observations', sectionKey: 'closing' },
];

const productionReportOrderFieldFallbackLabels: Partial<
  Record<ProductionReportOrderFieldKey, string>
> = {
  manufacturing_order_number: 'OF',
  product: 'Producto',
  customer: 'Cliente',
  customer_order: 'Pedido cliente',
  planned_quantity: 'Cantidad prevista',
  actual_quantity: 'Cantidad real',
  speed: 'Velocidad',
  observations: 'Observaciones',
};

function resolveDefaultAdditionalFieldLayoutSection(
  field: ProductionAdditionalFieldConfig,
): ProductionReportFormLayoutSectionKey {
  return field.section_key ?? (field.field_type === 'number' ? 'production' : 'identity');
}

function buildDefaultProductionReportFormLayoutConfig(
  operationForm: UpdateProductionOperationSettingsPayload,
): ProductionReportFormLayoutConfig {
  const fieldRefsBySection = new Map<
    ProductionReportFormLayoutSectionKey,
    ProductionReportFormLayoutConfig['sections'][number]['field_refs']
  >(
    productionReportFormLayoutSectionDefinitions.map((section) => [
      section.key,
      [],
    ]),
  );

  defaultProductionReportOrderFieldLayout.forEach((field) => {
    const config = operationForm.order_fields_config[field.key];

    if (!config?.visible) {
      return;
    }

    fieldRefsBySection.get(field.sectionKey)?.push({
      source: 'order_field',
      key: field.key,
    });
  });

  operationForm.additional_fields_config
    .filter((field) => field.is_active && field.visible)
    .sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0))
    .forEach((field) => {
      const sectionKey = resolveDefaultAdditionalFieldLayoutSection(field);

      fieldRefsBySection.get(sectionKey)?.push({
        source: 'additional_field',
        key: field.key,
      });
    });

  return {
    sections: productionReportFormLayoutSectionDefinitions.map((section) => ({
      key: section.key,
      label: section.label,
      field_refs: fieldRefsBySection.get(section.key) ?? [],
    })),
  };
}

function normalizeProductionReportFormLayoutConfig(
  operationForm: UpdateProductionOperationSettingsPayload,
): ProductionReportFormLayoutConfig {
  const defaultLayout = buildDefaultProductionReportFormLayoutConfig(operationForm);
  const savedLayout = operationForm.production_report_form_layout_config;

  if (!savedLayout?.sections?.length) {
    return defaultLayout;
  }

  const visibleDefaultRefKeys = new Set(
    defaultLayout.sections.flatMap((section) =>
      section.field_refs.map((fieldRef) => `${fieldRef.source}:${fieldRef.key}`),
    ),
  );
  const usedRefKeys = new Set<string>();
  const savedSectionsByKey = new Map(
    savedLayout.sections.map((section) => [section.key, section]),
  );

  const sections = productionReportFormLayoutSectionDefinitions.map((sectionDefinition) => {
    const savedSection = savedSectionsByKey.get(sectionDefinition.key);
    const savedRefs = savedSection?.field_refs ?? [];

    const fieldRefs = savedRefs.filter((fieldRef) => {
      const refKey = `${fieldRef.source}:${fieldRef.key}`;

      if (!visibleDefaultRefKeys.has(refKey)) {
        return false;
      }

      if (usedRefKeys.has(refKey)) {
        return false;
      }

      usedRefKeys.add(refKey);
      return true;
    });

    return {
      key: sectionDefinition.key,
      label: savedSection?.label || sectionDefinition.label,
      field_refs: fieldRefs,
    };
  });

  defaultLayout.sections.forEach((defaultSection) => {
    const targetSection = sections.find((section) => section.key === defaultSection.key);

    if (!targetSection) {
      return;
    }

    defaultSection.field_refs.forEach((fieldRef) => {
      const refKey = `${fieldRef.source}:${fieldRef.key}`;

      if (usedRefKeys.has(refKey)) {
        return;
      }

      targetSection.field_refs.push(fieldRef);
      usedRefKeys.add(refKey);
    });
  });

  return { sections };
}

function SectionHeader({
  title,
  description,
  actionLabel,
  onAction,
}: {
  title: string;
  description: string;
  actionLabel: string;
  onAction: () => void;
}) {
  return (
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      justifyContent="space-between"
      alignItems={{ xs: 'stretch', sm: 'flex-start' }}
      spacing={1.5}
    >
      <Stack spacing={0.4}>
        <Typography sx={{ color: '#f8fafc', fontWeight: 950, fontSize: '1.05rem' }}>
          {title}
        </Typography>
        <Typography sx={{ color: '#94a3b8', fontSize: '0.92rem' }}>
          {description}
        </Typography>
      </Stack>

      <Button variant="contained" startIcon={<AddRoundedIcon />} onClick={onAction}>
        {actionLabel}
      </Button>
    </Stack>
  );
}

function ActiveChip({ active }: { active: boolean }) {
  return (
    <Chip
      size="small"
      label={active ? 'Activo' : 'Inactivo'}
      color={active ? 'success' : 'default'}
      variant="outlined"
    />
  );
}

function normalizeOptionalFormText(value: string) {
  const normalized = value.trim();
  return normalized || null;
}

function parseOptionalNonNegativeFormNumber(value: string, label: string) {
  const normalized = value.trim();

  if (!normalized) {
    return null;
  }

  const parsedValue = Number(normalized);

  if (!Number.isFinite(parsedValue) || parsedValue < 0) {
    throw new Error(`${label} debe ser un número válido y no negativo.`);
  }

  return parsedValue;
}

function buildMaterialLotPayloadFromForm(
  form: MaterialLotForm,
): CreateProductionMaterialLotPayload {
  const materialName = form.material_name.trim();
  const lotNumber = form.lot_number.trim();

  if (!materialName) {
    throw new Error('El lote debe tener material informado.');
  }

  if (!lotNumber) {
    throw new Error('El lote debe tener número de lote informado.');
  }

  return {
    material_name: materialName,
    lot_number: lotNumber,
    supplier_name: normalizeOptionalFormText(form.supplier_name),
    supplier_lot_number: normalizeOptionalFormText(form.supplier_lot_number),
    received_at: normalizeOptionalFormText(form.received_at),
    expiration_date: normalizeOptionalFormText(form.expiration_date),
    quantity_received: parseOptionalNonNegativeFormNumber(
      form.quantity_received,
      'La cantidad recibida',
    ),
    quantity_available: parseOptionalNonNegativeFormNumber(
      form.quantity_available,
      'La cantidad disponible',
    ),
    quantity_unit: normalizeOptionalFormText(form.quantity_unit),
    status: form.status,
    quality_status: form.quality_status,
    source_type: 'manual',
    notes: normalizeOptionalFormText(form.notes),
    extra_fields: {},
  };
}

function buildMaterialLotFormFromLot(lot: ProductionMaterialLot): MaterialLotForm {
  return {
    id: lot.id,
    material_name: lot.material_name,
    lot_number: lot.lot_number,
    supplier_name: lot.supplier_name ?? '',
    supplier_lot_number: lot.supplier_lot_number ?? '',
    received_at: lot.received_at ?? '',
    expiration_date: lot.expiration_date ?? '',
    quantity_received:
      lot.quantity_received === null || lot.quantity_received === undefined
        ? ''
        : String(lot.quantity_received),
    quantity_available:
      lot.quantity_available === null || lot.quantity_available === undefined
        ? ''
        : String(lot.quantity_available),
    quantity_unit: lot.quantity_unit ?? '',
    status: lot.status,
    quality_status: lot.quality_status,
    notes: lot.notes ?? '',
  };
}

function formatMaterialLotQuantity(lot: ProductionMaterialLot) {
  if (lot.quantity_available === null || lot.quantity_available === undefined) {
    return '—';
  }

  return `${lot.quantity_available}${lot.quantity_unit ? ` ${lot.quantity_unit}` : ''}`;
}

export default function IndustrialCostProductionReportsConfigView() {
  const queryClient = useQueryClient();
  // IND-007
  const canImport = useCanManageImports();
  const { enqueueSnackbar } = useSnackbar();

  const [tab, setTab] = useState<ConfigTab>('lines');

  const [lineGroupDialogOpen, setLineGroupDialogOpen] = useState(false);
  const [lineGroupDialogMode, setLineGroupDialogMode] = useState<DialogMode>('create');
  const [lineGroupForm, setLineGroupForm] = useState<LineGroupForm>(emptyLineGroupForm);

  const [lineDialogOpen, setLineDialogOpen] = useState(false);
  const [lineDialogMode, setLineDialogMode] = useState<DialogMode>('create');
  const [lineForm, setLineForm] = useState<LineForm>(emptyLineForm);

  const [shiftDialogOpen, setShiftDialogOpen] = useState(false);
  const [shiftDialogMode, setShiftDialogMode] = useState<DialogMode>('create');
  const [shiftForm, setShiftForm] = useState<ShiftForm>(emptyShiftForm);

  const [stopReasonDialogOpen, setStopReasonDialogOpen] = useState(false);
  const [stopReasonDialogMode, setStopReasonDialogMode] = useState<DialogMode>('create');
  const [stopReasonForm, setStopReasonForm] = useState<StopReasonForm>(emptyStopReasonForm);

  const [qualityControlDialogOpen, setQualityControlDialogOpen] = useState(false);
  const [qualityControlDialogMode, setQualityControlDialogMode] = useState<DialogMode>('create');
  const [qualityControlForm, setQualityControlForm] =
    useState<QualityControlForm>(emptyQualityControlForm);
  const [plantUserDialogOpen, setPlantUserDialogOpen] = useState(false);
  const [plantUserDialogMode] = useState<DialogMode>('create');
  const [plantUserForm, setPlantUserForm] =
    useState<PlantUserForm>(emptyPlantUserForm);

  const [plantUserPasswordDialogOpen, setPlantUserPasswordDialogOpen] = useState(false);
  const [plantUserPasswordForm, setPlantUserPasswordForm] =
    useState<PlantUserPasswordForm>(emptyPlantUserPasswordForm);
  const [materialLotForm, setMaterialLotForm] =
    useState<MaterialLotForm>(emptyMaterialLotForm);
  const [
    importedManufacturingOrdersImportFlow,
    setImportedManufacturingOrdersImportFlow,
  ] = useState(createImportedManufacturingOrdersImportFlowState());

  const importedManufacturingOrdersFile = importedManufacturingOrdersImportFlow.file;
  const importedManufacturingOrdersPreview = importedManufacturingOrdersImportFlow.preview;
  const importedManufacturingOrdersConfirmResult =
    importedManufacturingOrdersImportFlow.confirmResult;

  const setImportedManufacturingOrdersFile = (file: File | null) => {
    setImportedManufacturingOrdersImportFlow(
      createImportedManufacturingOrdersImportFlowState(file),
    );
  };

  const [
    importedManufacturingOrdersNormalizationResult,
    setImportedManufacturingOrdersNormalizationResult,
  ] = useState<FileNormalizationAnalyzeResult | null>(null);

  const [
    importedManufacturingOrdersNormalizationReviewOpen,
    setImportedManufacturingOrdersNormalizationReviewOpen,
  ] = useState(false);
  const [
    importedManufacturingOrdersNormalizationReviewSheetName,
    setImportedManufacturingOrdersNormalizationReviewSheetName,
  ] = useState('');
  const [
    importedManufacturingOrdersNormalizationReviewHeaderRowInput,
    setImportedManufacturingOrdersNormalizationReviewHeaderRowInput,
  ] = useState('1');

  const [
    importedManufacturingOrdersImportDialogOpen,
    setImportedManufacturingOrdersImportDialogOpen,
  ] = useState(false);
  const [
    importedManufacturingOrdersPlanningMappingOpen,
    setImportedManufacturingOrdersPlanningMappingOpen,
  ] = useState(false);

  const setImportedManufacturingOrdersPreview = (
    preview: ImportedManufacturingOrderImportPreviewResponse | null,
  ) => {
    setImportedManufacturingOrdersImportFlow((current) => ({
      ...current,
      preview,
    }));
  };

  const setImportedManufacturingOrdersConfirmResult = (
    confirmResult: ImportedManufacturingOrderImportConfirmResponse | null,
  ) => {
    setImportedManufacturingOrdersImportFlow((current) => ({
      ...current,
      confirmResult,
    }));
  };

  const linesQuery = useQuery({
    queryKey: ['industrial-cost-production-config-lines'],
    queryFn: listIndustrialCostProductionLinesConfig,
  });

  const lineGroupsQuery = useQuery({
    queryKey: ['industrial-cost-production-config-line-groups'],
    queryFn: listIndustrialCostProductionLineGroupsConfig,
  });

  const shiftsQuery = useQuery({
    queryKey: ['industrial-cost-production-config-shifts'],
    queryFn: listIndustrialCostProductionShiftsConfig,
  });

  const stopReasonsQuery = useQuery({
    queryKey: ['industrial-cost-production-config-stop-reasons'],
    queryFn: listIndustrialCostProductionReportStopReasonsConfig,
  });

  const qualityControlsQuery = useQuery({
    queryKey: ['industrial-cost-production-config-quality-controls'],
    queryFn: () => listIndustrialCostProductionReportQualityControlsConfig(),
  });

  const operationSettingsQuery = useQuery<ProductionOperationSettings>({
    queryKey: ['industrial-cost-production-config-operation-settings'],
    queryFn: getIndustrialCostProductionOperationSettingsConfig,
  });

  const productionMaterialLotsQuery = useQuery<ProductionMaterialLot[]>({
    queryKey: ['industrial-cost-production-material-lots'],
    queryFn: () =>
      listIndustrialCostProductionMaterialLots({
        include_unavailable: true,
        limit: 100,
      }),
  });

  const [operationForm, setOperationForm] =
    useState<UpdateProductionOperationSettingsPayload | null>(null);
  const [draggedOrderFieldKey, setDraggedOrderFieldKey] = useState<string | null>(null);
  const [draggedAdditionalFieldKey, setDraggedAdditionalFieldKey] = useState<string | null>(null);
  const [
    draggedProductionReportLayoutFieldRef,
    setDraggedProductionReportLayoutFieldRef,
  ] = useState<
    ProductionReportFormLayoutConfig['sections'][number]['field_refs'][number] | null
  >(null);
  const [draggedProductionLineGroupId, setDraggedProductionLineGroupId] = useState<string | null>(null);
  const [draggedProductionLineId, setDraggedProductionLineId] = useState<string | null>(null);

  const operatorContextQuery = useQuery({
    queryKey: ['industrial-cost-production-config-operator-context'],
    queryFn: () => getIndustrialCostProductionReportOperatorContext({}),
  });

  const templates = operatorContextQuery.data?.templates ?? [];
  const lines = useMemo(
    () => [...(linesQuery.data ?? [])].sort((a, b) => a.display_order - b.display_order),
    [linesQuery.data],
  );
  const lineGroups = useMemo(
    () =>
      [...(lineGroupsQuery.data ?? [])].sort(
        (a, b) =>
          (a.display_order ?? 0) - (b.display_order ?? 0) ||
          a.name.localeCompare(b.name),
      ),
    [lineGroupsQuery.data],
  );
  const lineGroupById = useMemo(() => {
    const map = new Map<string, ProductionLineGroup>();
    lineGroups.forEach((group) => map.set(group.id, group));
    return map;
  }, [lineGroups]);

  const lineProcessGroups = useMemo(() => {
    const groups = new Map<
      string,
      {
        key: string;
        label: string;
        lines: ProductionLineConfig[];
        firstDisplayOrder: number;
        groupDisplayOrder: number;
      }
    >();

    lines.forEach((line) => {
      const managedGroup = line.production_line_group_id
        ? lineGroupById.get(line.production_line_group_id)
        : null;
      const rawGroup =
        managedGroup?.name?.trim() ||
        line.production_line_group_name?.trim() ||
        line.process_group?.trim();
      const key = line.production_line_group_id || rawGroup || '__ungrouped__';
      const label = rawGroup || 'Sin proceso asignado';
      const groupDisplayOrder = managedGroup?.display_order ?? 999999;
      const existingGroup = groups.get(key);

      if (existingGroup) {
        existingGroup.lines.push(line);
        existingGroup.firstDisplayOrder = Math.min(
          existingGroup.firstDisplayOrder,
          line.display_order ?? 0,
        );
        existingGroup.groupDisplayOrder = Math.min(
          existingGroup.groupDisplayOrder,
          groupDisplayOrder,
        );
      } else {
        groups.set(key, {
          key,
          label,
          lines: [line],
          firstDisplayOrder: line.display_order ?? 0,
          groupDisplayOrder,
        });
      }
    });

    return Array.from(groups.values()).sort(
      (a, b) =>
        a.groupDisplayOrder - b.groupDisplayOrder ||
        a.firstDisplayOrder - b.firstDisplayOrder ||
        a.label.localeCompare(b.label),
    );
  }, [lineGroupById, lines]);

  const shifts = shiftsQuery.data ?? [];
  const stopReasons = stopReasonsQuery.data ?? [];
  const qualityControls = qualityControlsQuery.data ?? [];
  const productionMaterialLots = productionMaterialLotsQuery.data ?? [];

  const templateById = useMemo(() => {
    const map = new Map<string, ProductionReportTemplate>();
    templates.forEach((template) => map.set(template.id, template));
    return map;
  }, [templates]);

  const numericAdditionalFields = useMemo(
    () =>
      [...(operationForm?.additional_fields_config ?? [])]
        .filter((field) => field.is_active && field.visible && field.field_type === 'number')
        .sort((a, b) => a.display_order - b.display_order),
    [operationForm?.additional_fields_config],
  );

  const invalidateConfig = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['industrial-cost-production-config-lines'] }),
      queryClient.invalidateQueries({ queryKey: ['industrial-cost-production-config-line-groups'] }),
      queryClient.invalidateQueries({ queryKey: ['industrial-cost-production-config-shifts'] }),
      queryClient.invalidateQueries({ queryKey: ['industrial-cost-production-config-stop-reasons'] }),
      queryClient.invalidateQueries({ queryKey: ['industrial-cost-production-config-quality-controls'] }),
      queryClient.invalidateQueries({ queryKey: ['industrial-cost-production-config-plant-users'] }),
      queryClient.invalidateQueries({ queryKey: ['industrial-cost-production-config-operator-context'] }),
      queryClient.invalidateQueries({ queryKey: ['industrial-cost-production-report-operator-context'] }),
      queryClient.invalidateQueries({ queryKey: ['industrial-cost-production-reports-factory-control'] }),
      queryClient.invalidateQueries({ queryKey: ['industrial-cost-production-material-lots'] }),
    ]);
  };

  const operationSettings = operationSettingsQuery.data ?? null;

  useEffect(() => {
    if (!operationSettings) return;

    setOperationForm({
      order_fields_config: operationSettings.order_fields_config,
      additional_fields_config: operationSettings.additional_fields_config ?? [],
      production_params: operationSettings.production_params,
      estimation_config: operationSettings.estimation_config,
      validation_rules: operationSettings.validation_rules,
      control_display_config: operationSettings.control_display_config,
      traceability_config: operationSettings.traceability_config,
      production_report_form_layout_config:
        operationSettings.production_report_form_layout_config ?? null,
    });
  }, [operationSettings]);

  const importedManufacturingOrdersMappableFields = useMemo(() => {
    const orderFieldsConfig = (operationForm?.order_fields_config ?? {}) as Record<
      string,
      { label?: string; visible?: boolean }
    >;

    const baseFields = [
      {
        key: 'manufacturing_order_number',
        configKey: 'manufacturing_order_number',
        defaultLabel: 'OF',
        required: true,
        fieldType: 'base' as const,
      },
      {
        key: 'planned_quantity_value',
        configKey: 'planned_quantity',
        defaultLabel: 'Cantidad prevista',
        required: true,
        fieldType: 'base' as const,
      },
      {
        key: 'free_product_ref',
        configKey: 'product',
        defaultLabel: 'Producto',
        required: false,
        fieldType: 'base' as const,
      },
      {
        key: 'customer_number',
        configKey: 'customer',
        defaultLabel: 'Cliente',
        required: false,
        fieldType: 'base' as const,
      },
      {
        key: 'customer_order_number',
        configKey: 'customer_order',
        defaultLabel: 'Pedido cliente',
        required: false,
        fieldType: 'base' as const,
      },
      {
        key: 'notes',
        configKey: 'observations',
        defaultLabel: 'Observaciones',
        required: false,
        fieldType: 'base' as const,
      },
      {
        key: 'speed_value',
        configKey: 'speed',
        defaultLabel: 'Velocidad',
        required: false,
        fieldType: 'base' as const,
      },
    ]
      .filter((field) => orderFieldsConfig[field.configKey]?.visible !== false)
      .map((field) => ({
        key: field.key,
        label: orderFieldsConfig[field.configKey]?.label?.trim() || field.defaultLabel,
        required: field.required,
        fieldType: field.fieldType,
      }));

    const additionalFields = (operationForm?.additional_fields_config ?? [])
      .filter((field) => field.is_active && field.visible)
      .sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0))
      .map((field) => ({
        key: field.key,
        label: field.label?.trim() || field.key,
        required: Boolean(field.required),
        fieldType: 'additional' as const,
      }));

    return [...baseFields, ...additionalFields];
  }, [operationForm]);

  const reorderOperationField = (sourceKey: string, targetKey: string) => {
    setOperationForm((current) => {
      if (!current || sourceKey === targetKey) return current;

      const entries = Object.entries(current.order_fields_config).sort(
        (a, b) => a[1].display_order - b[1].display_order,
      );

      const sourceIndex = entries.findIndex(([key]) => key === sourceKey);
      const targetIndex = entries.findIndex(([key]) => key === targetKey);

      if (sourceIndex === -1 || targetIndex === -1) return current;

      const reordered = [...entries];
      const [moved] = reordered.splice(sourceIndex, 1);
      reordered.splice(targetIndex, 0, moved);

      const nextOrderFieldsConfig = reordered.reduce<
        UpdateProductionOperationSettingsPayload['order_fields_config']
      >((acc, [key, value], index) => {
        acc[key as keyof UpdateProductionOperationSettingsPayload['order_fields_config']] = {
          ...value,
          display_order: (index + 1) * 10,
        };
        return acc;
      }, { ...current.order_fields_config });

      return {
        ...current,
        order_fields_config: nextOrderFieldsConfig,
      };
    });
  };

  const addAdditionalOperationField = () => {
    setOperationForm((current) => {
      if (!current) return current;

      const existingFields = current.additional_fields_config ?? [];
      const baseIndex = existingFields.length + 1;
      const baseKey = `campo_${baseIndex}`;

      let nextKey = baseKey;
      let suffix = 2;

      while (existingFields.some((field) => field.key === nextKey)) {
        nextKey = `${baseKey}_${suffix}`;
        suffix += 1;
      }

      const nextField: ProductionAdditionalFieldConfig = {
        key: nextKey,
        label: `Campo ${baseIndex}`,
        field_type: 'text',
        section_key: 'additional',
        visible: true,
        required: false,
        display_order:
          existingFields.length > 0
            ? Math.max(...existingFields.map((field) => field.display_order ?? 0)) + 10
            : 10,
        is_active: true,
      };

      return {
        ...current,
        additional_fields_config: [...existingFields, nextField],
      };
    });
  };

  const updateAdditionalOperationField = (
    fieldKey: string,
    patch: Partial<ProductionAdditionalFieldConfig>,
  ) => {
    setOperationForm((current) =>
      current
        ? {
            ...current,
            additional_fields_config: current.additional_fields_config.map((field) =>
              field.key === fieldKey
                ? {
                    ...field,
                    ...patch,
                  }
                : field,
            ),
          }
        : current,
    );
  };

  const removeAdditionalOperationField = (fieldKey: string) => {
    setOperationForm((current) =>
      current
        ? {
            ...current,
            additional_fields_config: current.additional_fields_config.filter(
              (field) => field.key !== fieldKey,
            ),
          }
        : current,
    );
  };

  const reorderAdditionalOperationField = (sourceKey: string, targetKey: string) => {
    setOperationForm((current) => {
      if (!current || sourceKey === targetKey) return current;

      const entries = [...current.additional_fields_config].sort(
        (a, b) => a.display_order - b.display_order,
      );

      const sourceIndex = entries.findIndex((field) => field.key === sourceKey);
      const targetIndex = entries.findIndex((field) => field.key === targetKey);

      if (sourceIndex === -1 || targetIndex === -1) return current;

      const reordered = [...entries];
      const [moved] = reordered.splice(sourceIndex, 1);
      reordered.splice(targetIndex, 0, moved);

      return {
        ...current,
        additional_fields_config: reordered.map((field, index) => ({
          ...field,
          display_order: (index + 1) * 10,
        })),
      };
    });
  };

  const updateTraceabilityConfig = (
    patch: Partial<UpdateProductionOperationSettingsPayload['traceability_config']>,
  ) => {
    setOperationForm((current) =>
      current
        ? {
            ...current,
            traceability_config: {
              ...current.traceability_config,
              ...patch,
            },
          }
        : current,
    );
  };

  const getProductionReportLayoutFieldRefKey = (
    fieldRef: ProductionReportFormLayoutConfig['sections'][number]['field_refs'][number],
  ) => `${fieldRef.source}:${fieldRef.key}`;

  const moveProductionReportLayoutField = (
    sourceFieldRef: ProductionReportFormLayoutConfig['sections'][number]['field_refs'][number],
    targetSectionKey: ProductionReportFormLayoutSectionKey,
    targetFieldRef?: ProductionReportFormLayoutConfig['sections'][number]['field_refs'][number],
  ) => {
    setOperationForm((current) => {
      if (!current) return current;

      const normalizedLayout = normalizeProductionReportFormLayoutConfig(current);
      const sourceRefKey = getProductionReportLayoutFieldRefKey(sourceFieldRef);
      const targetRefKey = targetFieldRef
        ? getProductionReportLayoutFieldRefKey(targetFieldRef)
        : null;

      let movedFieldRef:
        | ProductionReportFormLayoutConfig['sections'][number]['field_refs'][number]
        | null = null;

      const nextSections = normalizedLayout.sections.map((section) => ({
        ...section,
        field_refs: section.field_refs.filter((fieldRef) => {
          const fieldRefKey = getProductionReportLayoutFieldRefKey(fieldRef);

          if (fieldRefKey !== sourceRefKey || movedFieldRef) {
            return true;
          }

          movedFieldRef = fieldRef;
          return false;
        }),
      }));

      if (!movedFieldRef) {
        return current;
      }

      const targetSectionIndex = nextSections.findIndex(
        (section) => section.key === targetSectionKey,
      );

      if (targetSectionIndex === -1) {
        return current;
      }

      const targetSection = nextSections[targetSectionIndex];
      const targetFieldRefs = [...targetSection.field_refs];

      let insertIndex = targetFieldRefs.length;

      if (targetRefKey) {
        const targetIndex = targetFieldRefs.findIndex(
          (fieldRef) => getProductionReportLayoutFieldRefKey(fieldRef) === targetRefKey,
        );

        if (targetIndex >= 0) {
          insertIndex = targetIndex;
        }
      }

      targetFieldRefs.splice(insertIndex, 0, movedFieldRef);

      nextSections[targetSectionIndex] = {
        ...targetSection,
        field_refs: targetFieldRefs,
      };

      return {
        ...current,
        production_report_form_layout_config: {
          sections: nextSections,
        },
      };
    });
  };

  const resetProductionReportLayoutConfig = () => {
    setOperationForm((current) =>
      current
        ? {
            ...current,
            production_report_form_layout_config: null,
          }
        : current,
    );
  };

  const saveLineGroupMutation = useMutation({
    mutationFn: (payload: LineGroupForm) => {
      const cleanPayload: UpsertProductionLineGroupPayload = {
        code: normalizeCode(payload.code || payload.name),
        name: payload.name.trim(),
        description: payload.description?.trim() || null,
        is_active: payload.is_active,
        display_order: Number(payload.display_order || 0),
      };

      if (payload.id) {
        return updateIndustrialCostProductionLineGroupConfig(payload.id, cleanPayload);
      }

      return createIndustrialCostProductionLineGroupConfig(cleanPayload);
    },
    onSuccess: async () => {
      enqueueSnackbar('Grupo de líneas guardado correctamente.', { variant: 'success' });
      setLineGroupDialogOpen(false);
      setLineGroupForm(emptyLineGroupForm);
      await invalidateConfig();
    },
    onError: (error) => enqueueSnackbar(getErrorMessage(error), { variant: 'error' }),
  });

  const deactivateLineGroupMutation = useMutation({
    mutationFn: deactivateIndustrialCostProductionLineGroupConfig,
    onSuccess: async () => {
      enqueueSnackbar('Grupo de líneas desactivado.', { variant: 'success' });
      await invalidateConfig();
    },
    onError: (error) => enqueueSnackbar(getErrorMessage(error), { variant: 'error' }),
  });

  const reorderLineGroupsMutation = useMutation({
    mutationFn: async (nextGroups: ProductionLineGroup[]) => {
      await Promise.all(
        nextGroups.map((group, index) =>
          updateIndustrialCostProductionLineGroupConfig(group.id, {
            code: group.code,
            name: group.name,
            description: group.description ?? null,
            is_active: group.is_active,
            display_order: (index + 1) * 10,
          }),
        ),
      );
    },
    onSuccess: async () => {
      enqueueSnackbar('Orden de grupos actualizado.', { variant: 'success' });
      await invalidateConfig();
    },
    onError: (error) => enqueueSnackbar(getErrorMessage(error), { variant: 'error' }),
  });

  const saveLineMutation = useMutation({
    mutationFn: (payload: LineForm) => {
      const selectedLineGroup = payload.production_line_group_id
        ? lineGroupById.get(payload.production_line_group_id)
        : null;

      const cleanPayload: UpsertProductionLineConfigPayload = {
        code: payload.code.trim(),
        name: payload.name.trim(),
        production_line_group_id: payload.production_line_group_id || null,
        process_group:
          selectedLineGroup?.name?.trim() ||
          payload.process_group?.trim() ||
          null,
        description: payload.description?.trim() || null,
        is_active: payload.is_active,
        display_order: Number(payload.display_order || 0),
      };

      if (payload.id) {
        return updateIndustrialCostProductionLineConfig(payload.id, cleanPayload);
      }

      return createIndustrialCostProductionLineConfig(cleanPayload);
    },
    onSuccess: async () => {
      enqueueSnackbar('Línea guardada correctamente.', { variant: 'success' });
      setLineDialogOpen(false);
      setLineForm(emptyLineForm);
      await invalidateConfig();
    },
    onError: (error) => enqueueSnackbar(getErrorMessage(error), { variant: 'error' }),
  });

  const deactivateLineMutation = useMutation({
    mutationFn: deactivateIndustrialCostProductionLineConfig,
    onSuccess: async () => {
      enqueueSnackbar('Línea desactivada.', { variant: 'success' });
      await invalidateConfig();
    },
    onError: (error) => enqueueSnackbar(getErrorMessage(error), { variant: 'error' }),
  });

  const reorderLinesMutation = useMutation({
    mutationFn: async (nextLines: ProductionLineConfig[]) => {
      await Promise.all(
        nextLines.map((line, index) =>
          updateIndustrialCostProductionLineConfig(line.id, {
            code: line.code,
            name: line.name,
            production_line_group_id: line.production_line_group_id ?? null,
            process_group:
              line.production_line_group_name ??
              line.process_group ??
              null,
            description: line.description ?? null,
            is_active: line.is_active,
            display_order: (index + 1) * 10,
          }),
        ),
      );
    },
    onSuccess: async () => {
      enqueueSnackbar('Orden de líneas actualizado.', { variant: 'success' });
      await invalidateConfig();
    },
    onError: (error) => enqueueSnackbar(getErrorMessage(error), { variant: 'error' }),
  });

  const saveShiftMutation = useMutation({
    mutationFn: (payload: ShiftForm) => {
      const cleanPayload: UpsertProductionShiftConfigPayload = {
        code: payload.code.trim(),
        label: payload.label.trim(),
        start_time: payload.start_time,
        end_time: payload.end_time,
        is_active: payload.is_active,
        display_order: Number(payload.display_order || 0),
      };

      if (payload.id) {
        return updateIndustrialCostProductionShiftConfig(payload.id, cleanPayload);
      }

      return createIndustrialCostProductionShiftConfig(cleanPayload);
    },
    onSuccess: async () => {
      enqueueSnackbar('Turno guardado correctamente.', { variant: 'success' });
      setShiftDialogOpen(false);
      setShiftForm(emptyShiftForm);
      await invalidateConfig();
    },
    onError: (error) => enqueueSnackbar(getErrorMessage(error), { variant: 'error' }),
  });

  const deactivateShiftMutation = useMutation({
    mutationFn: deactivateIndustrialCostProductionShiftConfig,
    onSuccess: async () => {
      enqueueSnackbar('Turno desactivado.', { variant: 'success' });
      await invalidateConfig();
    },
    onError: (error) => enqueueSnackbar(getErrorMessage(error), { variant: 'error' }),
  });

  const saveStopReasonMutation = useMutation({
    mutationFn: (payload: StopReasonForm) => {
      const cleanPayload: UpsertProductionReportStopReasonPayload = {
        code: payload.code.trim(),
        label: payload.label.trim(),
        is_active: payload.is_active,
        display_order: Number(payload.display_order || 0),
      };

      if (payload.id) {
        return updateIndustrialCostProductionReportStopReasonConfig(payload.id, cleanPayload);
      }

      return createIndustrialCostProductionReportStopReasonConfig(cleanPayload);
    },
    onSuccess: async () => {
      enqueueSnackbar('Motivo guardado correctamente.', { variant: 'success' });
      setStopReasonDialogOpen(false);
      setStopReasonForm(emptyStopReasonForm);
      await invalidateConfig();
    },
    onError: (error) => enqueueSnackbar(getErrorMessage(error), { variant: 'error' }),
  });

  const deactivateStopReasonMutation = useMutation({
    mutationFn: deactivateIndustrialCostProductionReportStopReasonConfig,
    onSuccess: async () => {
      enqueueSnackbar('Motivo desactivado.', { variant: 'success' });
      await invalidateConfig();
    },
    onError: (error) => enqueueSnackbar(getErrorMessage(error), { variant: 'error' }),
  });

  const saveQualityControlMutation = useMutation({
    mutationFn: (payload: QualityControlForm) => {
      const cleanPayload: UpsertProductionReportQualityControlPayload = {
        template_id: payload.template_id,
        code: payload.code.trim(),
        label: payload.label.trim(),
        description: payload.description?.trim() || null,
        is_active: payload.is_active,
        display_order: Number(payload.display_order || 0),
      };

      if (payload.id) {
        return updateIndustrialCostProductionReportQualityControlConfig(payload.id, cleanPayload);
      }

      return createIndustrialCostProductionReportQualityControlConfig(cleanPayload);
    },
    onSuccess: async () => {
      enqueueSnackbar('Control guardado correctamente.', { variant: 'success' });
      setQualityControlDialogOpen(false);
      setQualityControlForm(emptyQualityControlForm);
      await invalidateConfig();
    },
    onError: (error) => enqueueSnackbar(getErrorMessage(error), { variant: 'error' }),
  });

  const deactivateQualityControlMutation = useMutation({
    mutationFn: deactivateIndustrialCostProductionReportQualityControlConfig,
    onSuccess: async () => {
      enqueueSnackbar('Control desactivado.', { variant: 'success' });
      await invalidateConfig();
    },
    onError: (error) => enqueueSnackbar(getErrorMessage(error), { variant: 'error' }),
  });

  const savePlantUserMutation = useMutation({
    mutationFn: (payload: PlantUserForm) => {
      if (payload.access_id) {
        const updatePayload: UpdatePlantUserPayload = {
          full_name: payload.full_name.trim() || null,
          role: payload.role,
          is_active: payload.is_active,
          permissions_override:
            payload.role === 'factory_manager'
              ? normalizePlantUserPermissionsOverride(payload.permissions_override)
              : {},
        };

        return updateIndustrialCostPlantUserConfig(payload.access_id, updatePayload);
      }

      const createPayload: CreatePlantUserPayload = {
        email: payload.email.trim().toLowerCase(),
        password: payload.password,
        full_name: payload.full_name.trim() || null,
        role: payload.role,
        is_active: payload.is_active,
      };

      return createIndustrialCostPlantUserConfig(createPayload);
    },
    onSuccess: async () => {
      enqueueSnackbar('Usuario de planta guardado correctamente.', { variant: 'success' });
      setPlantUserDialogOpen(false);
      setPlantUserForm(emptyPlantUserForm);
      await invalidateConfig();
    },
    onError: (error) => enqueueSnackbar(getErrorMessage(error), { variant: 'error' }),
  });

  const resetPlantUserPasswordMutation = useMutation({
    mutationFn: ({
      accessId,
      payload,
    }: {
      accessId: string;
      payload: ResetPlantUserPasswordPayload;
    }) => resetIndustrialCostPlantUserPasswordConfig(accessId, payload),
    onSuccess: async () => {
      enqueueSnackbar('Contraseña actualizada correctamente.', { variant: 'success' });
      setPlantUserPasswordDialogOpen(false);
      setPlantUserPasswordForm(emptyPlantUserPasswordForm);
      await invalidateConfig();
    },
    onError: (error) => enqueueSnackbar(getErrorMessage(error), { variant: 'error' }),
  });

  const saveOperationSettingsMutation = useMutation({
    mutationFn: updateIndustrialCostProductionOperationSettingsConfig,
    onSuccess: () => {
      enqueueSnackbar('Configuración operativa guardada.', { variant: 'success' });
      queryClient.invalidateQueries({
        queryKey: ['industrial-cost-production-config-operation-settings'],
      });
    },
    onError: (error) => {
      enqueueSnackbar(getErrorMessage(error), { variant: 'error' });
    },
  });

  const saveProductionMaterialLotMutation = useMutation({
    mutationFn: (form: MaterialLotForm) => {
      const payload = buildMaterialLotPayloadFromForm(form);

      if (form.id) {
        return updateIndustrialCostProductionMaterialLot(form.id, payload);
      }

      return createIndustrialCostProductionMaterialLot(payload);
    },
    onSuccess: async () => {
      enqueueSnackbar('Lote controlado guardado correctamente.', {
        variant: 'success',
      });
      setMaterialLotForm(emptyMaterialLotForm);
      await queryClient.invalidateQueries({
        queryKey: ['industrial-cost-production-material-lots'],
      });
    },
    onError: (error) => {
      enqueueSnackbar(getErrorMessage(error), { variant: 'error' });
    },
  });

  const previewImportedManufacturingOrdersImportMutation = useMutation({
    mutationFn: async ({
      file,
      mode,
      sheetName,
      headerRowIndex,
    }: {
      file: File;
      mode: 'auto' | 'manual_review';
      sheetName?: string | null;
      headerRowIndex?: number | null;
    }) => {
      const normalization =
        mode === 'manual_review'
          ? await reviewFileNormalization(
              file,
              'imported_manufacturing_orders',
              headerRowIndex ?? 0,
              sheetName || null,
            )
          : await analyzeFileNormalization(file, 'imported_manufacturing_orders');

      const nextFlow = await runImportedManufacturingOrdersImportPreview(
        createImportedManufacturingOrdersImportFlowState(file),
        {
          file,
          normalization: {
            sheet_name: normalization.selected_sheet,
            header_row_index: normalization.header_row_index,
          },
          mapping: normalization.applied_column_mapping ?? undefined,
        },
      );

      return {
        nextFlow,
        normalization,
      };
    },
    onSuccess: ({ nextFlow, normalization }) => {
      setImportedManufacturingOrdersImportFlow(nextFlow);
      setImportedManufacturingOrdersNormalizationResult(normalization);
      setImportedManufacturingOrdersNormalizationReviewOpen(false);
      setImportedManufacturingOrdersNormalizationReviewSheetName(
        normalization.selected_sheet ?? '',
      );
      setImportedManufacturingOrdersNormalizationReviewHeaderRowInput(
        String(normalization.header_row_index + 1),
      );
      enqueueSnackbar('Previsualización de OFs generada correctamente.', {
        variant: 'success',
      });
    },
    onError: (error) => {
      enqueueSnackbar(getErrorMessage(error), { variant: 'error' });
    },
  });

  const confirmImportedManufacturingOrdersImportMutation = useMutation({
    mutationFn: async ({
      file,
      mapping,
    }: {
      file: File;
      mapping: Record<string, string | null>;
    }) =>
      confirmImportedManufacturingOrdersImport({
        ...importedManufacturingOrdersImportFlow,
        file,
        mapping,
      }),
    onSuccess: async (nextFlow) => {
      setImportedManufacturingOrdersImportFlow(nextFlow);

      try {
        const normalization = importedManufacturingOrdersImportFlow.normalization;
        const headerRowIndex = normalization?.header_row_index;

        if (normalization && typeof headerRowIndex === 'number') {
          const effectiveMapping =
            nextFlow.mapping && Object.keys(nextFlow.mapping).length > 0
              ? nextFlow.mapping
              : importedManufacturingOrdersImportFlow.mapping;

          await commitFileNormalization({
            importer_key: 'imported_manufacturing_orders',
            sheet_name: normalization.sheet_name ?? null,
            header_row_index: headerRowIndex,
            data_start_row_index: headerRowIndex + 1,
            column_mapping: Object.fromEntries(
              Object.entries(effectiveMapping).filter(
                ([, value]) => value !== null && value !== '',
              ),
            ) as Record<string, string>,
            known_columns: importedManufacturingOrdersPreview?.columns ?? [],
          });
        }
      } catch (error) {
        enqueueSnackbar(
          `La importación se completó, pero no se pudo guardar la configuración recordada: ${getErrorMessage(
            error,
          )}`,
          { variant: 'warning' },
        );
      }

      enqueueSnackbar('Importación de OFs completada correctamente.', {
        variant: 'success',
      });

      setImportedManufacturingOrdersImportDialogOpen(false);

      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ['industrial-cost-production-existing-orders-candidates'],
        }),
        queryClient.invalidateQueries({
          queryKey: ['industrial-cost-production-planning-context'],
        }),
        queryClient.invalidateQueries({
          queryKey: ['industrial-cost-production-planning-week-context'],
        }),
        queryClient.invalidateQueries({
          queryKey: ['industrial-cost-plant-dashboard'],
        }),
        queryClient.invalidateQueries({
          queryKey: ['industrial-cost-material-stock-projection'],
        }),
      ]);
    },
    onError: (error) => {
      enqueueSnackbar(getErrorMessage(error), { variant: 'error' });
    },
  });

  const openCreateLineGroup = () => {
    const nextDisplayOrder =
      lineGroups.length > 0
        ? Math.max(...lineGroups.map((group) => group.display_order ?? 0)) + 10
        : 10;

    setLineGroupDialogMode('create');
    setLineGroupForm({
      ...emptyLineGroupForm,
      display_order: nextDisplayOrder,
    });
    setLineGroupDialogOpen(true);
  };

  const openEditLineGroup = (group: ProductionLineGroup) => {
    setLineGroupDialogMode('edit');
    setLineGroupForm({
      id: group.id,
      code: group.code,
      name: group.name,
      description: group.description ?? '',
      is_active: group.is_active,
      display_order: group.display_order,
    });
    setLineGroupDialogOpen(true);
  };

  const reorderProductionLineGroup = (sourceId: string, targetId: string) => {
    if (sourceId === targetId || reorderLineGroupsMutation.isPending) return;

    const sourceIndex = lineGroups.findIndex((group) => group.id === sourceId);
    const targetIndex = lineGroups.findIndex((group) => group.id === targetId);

    if (sourceIndex === -1 || targetIndex === -1) return;

    const reorderedGroups = [...lineGroups];
    const [movedGroup] = reorderedGroups.splice(sourceIndex, 1);
    reorderedGroups.splice(targetIndex, 0, movedGroup);

    reorderLineGroupsMutation.mutate(reorderedGroups);
  };

  const openCreateLine = () => {
    const nextDisplayOrder =
      lines.length > 0 ? Math.max(...lines.map((line) => line.display_order ?? 0)) + 10 : 10;

    setLineDialogMode('create');
    setLineForm({
      ...emptyLineForm,
      display_order: nextDisplayOrder,
    });
    setLineDialogOpen(true);
  };

  const openEditLine = (line: ProductionLineConfig) => {
    setLineDialogMode('edit');
    setLineForm({
      id: line.id,
      code: line.code,
      name: line.name,
      production_line_group_id: line.production_line_group_id ?? null,
      process_group:
        line.production_line_group_name ??
        line.process_group ??
        '',
      description: line.description ?? '',
      is_active: line.is_active,
      display_order: line.display_order,
    });
    setLineDialogOpen(true);
  };

  const reorderProductionLine = (sourceId: string, targetId: string) => {
    if (sourceId === targetId || reorderLinesMutation.isPending) return;

    const sourceIndex = lines.findIndex((line) => line.id === sourceId);
    const targetIndex = lines.findIndex((line) => line.id === targetId);

    if (sourceIndex === -1 || targetIndex === -1) return;

    const reorderedLines = [...lines];
    const [movedLine] = reorderedLines.splice(sourceIndex, 1);
    reorderedLines.splice(targetIndex, 0, movedLine);

    reorderLinesMutation.mutate(reorderedLines);
  };

  const openCreateShift = () => {
    setShiftDialogMode('create');
    setShiftForm(emptyShiftForm);
    setShiftDialogOpen(true);
  };

  const openEditShift = (shift: ProductionShiftConfig) => {
    setShiftDialogMode('edit');
    setShiftForm({
      id: shift.id,
      code: shift.code,
      label: shift.label,
      start_time: shift.start_time.slice(0, 5),
      end_time: shift.end_time.slice(0, 5),
      is_active: shift.is_active,
      display_order: shift.display_order,
    });
    setShiftDialogOpen(true);
  };

  const openCreateStopReason = () => {
    setStopReasonDialogMode('create');
    setStopReasonForm(emptyStopReasonForm);
    setStopReasonDialogOpen(true);
  };

  const openEditStopReason = (reason: ProductionReportStopReason) => {
    setStopReasonDialogMode('edit');
    setStopReasonForm({
      id: reason.id,
      code: reason.code,
      label: reason.label,
      is_active: reason.is_active,
      display_order: reason.display_order,
    });
    setStopReasonDialogOpen(true);
  };

  const openCreateQualityControl = () => {
    setQualityControlDialogMode('create');
    setQualityControlForm({
      ...emptyQualityControlForm,
      template_id: templates[0]?.id ?? '',
    });
    setQualityControlDialogOpen(true);
  };

  const openEditQualityControl = (control: ProductionReportQualityControl) => {
    setQualityControlDialogMode('edit');
    setQualityControlForm({
      id: control.id,
      template_id: control.template_id,
      code: control.code,
      label: control.label,
      description: control.description ?? '',
      is_active: control.is_active,
      display_order: control.display_order,
    });
    setQualityControlDialogOpen(true);
  };

  const renderRows = <T extends { id: string; is_active: boolean; display_order: number }>(
    rows: T[],
    renderMain: (row: T) => React.ReactNode,
    onEdit: (row: T) => void,
    onDeactivate: (id: string) => void,
  ) => (
    <Stack spacing={1}>
      {rows.length === 0 ? (
        <Alert severity="info" variant="outlined">
          No hay registros configurados.
        </Alert>
      ) : (
        rows.map((row) => (
          <Paper
            key={row.id}
            elevation={0}
            sx={{
              borderRadius: 3,
              border: '1px solid rgba(148,163,184,0.14)',
              bgcolor: 'rgba(2,6,23,0.28)',
              px: 1.5,
              py: 1.25,
            }}
          >
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              justifyContent="space-between"
              spacing={1.25}
            >
              <Stack spacing={0.4}>
                {renderMain(row)}
                <Stack direction="row" spacing={1} alignItems="center">
                  <ActiveChip active={row.is_active} />
                  <Typography sx={{ color: '#64748b', fontSize: '0.82rem' }}>
                    Orden {row.display_order}
                  </Typography>
                </Stack>
              </Stack>

              <Stack direction="row" spacing={1} alignItems="center">
                <Button
                  size="small"
                  startIcon={<EditRoundedIcon />}
                  onClick={() => onEdit(row)}
                >
                  Editar
                </Button>

                <Button
                  size="small"
                  color="warning"
                  startIcon={<DeleteOutlineRoundedIcon />}
                  disabled={!row.is_active}
                  onClick={() => onDeactivate(row.id)}
                >
                  Desactivar
                </Button>
              </Stack>
            </Stack>
          </Paper>
        ))
      )}
    </Stack>
  );

  const renderLines = () => (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 4,
        border: '1px solid rgba(148,163,184,0.18)',
        bgcolor: 'rgba(15,23,42,0.66)',
        p: { xs: 2.5, md: 3 },
      }}
    >
      <Stack spacing={2}>
        <SectionHeader
          title="Líneas / puestos"
          description="Define líneas, máquinas y el grupo operativo al que pertenecen. El orden se gestiona arrastrando desde la propia lista."
          actionLabel="Nueva línea"
          onAction={openCreateLine}
        />

        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            border: '1px solid rgba(56,189,248,0.18)',
            bgcolor: 'rgba(8,47,73,0.16)',
            p: 1.5,
          }}
        >
          <Stack spacing={1.25}>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              justifyContent="space-between"
              alignItems={{ xs: 'stretch', md: 'flex-start' }}
              spacing={1.25}
            >
              <Stack spacing={0.25}>
                <Typography sx={{ color: '#f8fafc', fontWeight: 900 }}>
                  Grupos de líneas
                </Typography>
                <Typography sx={{ color: '#94a3b8', fontSize: '0.88rem' }}>
                  Crea y mantiene los grupos operativos usados para organizar las líneas, por ejemplo EPDM o TPE.
                </Typography>
              </Stack>

              <Button
                size="small"
                variant="outlined"
                startIcon={<AddRoundedIcon />}
                onClick={openCreateLineGroup}
              >
                Nuevo grupo
              </Button>
            </Stack>

            {lineGroupsQuery.isFetching && <LinearProgress />}

            {lineGroups.length === 0 ? (
              <Alert severity="info" variant="outlined">
                No hay grupos de líneas configurados.
              </Alert>
            ) : (
              <Stack spacing={1}>
                {lineGroups.map((group) => {
                  const linkedLinesCount = lines.filter(
                    (line) => line.production_line_group_id === group.id,
                  ).length;

                  return (
                    <Paper
                      key={group.id}
                      elevation={0}
                      draggable
                      onDragStart={() => setDraggedProductionLineGroupId(group.id)}
                      onDragOver={(event) => {
                        event.preventDefault();
                      }}
                      onDrop={() => {
                        if (draggedProductionLineGroupId) {
                          reorderProductionLineGroup(draggedProductionLineGroupId, group.id);
                        }
                        setDraggedProductionLineGroupId(null);
                      }}
                      onDragEnd={() => setDraggedProductionLineGroupId(null)}
                      sx={{
                        borderRadius: 3,
                        border:
                          draggedProductionLineGroupId === group.id
                            ? '1px solid rgba(56,189,248,0.55)'
                            : '1px solid rgba(148,163,184,0.14)',
                        bgcolor:
                          draggedProductionLineGroupId === group.id
                            ? 'rgba(8,47,73,0.24)'
                            : 'rgba(2,6,23,0.28)',
                        px: 1.5,
                        py: 1.25,
                        cursor: 'grab',
                      }}
                    >
                      <Stack
                        direction={{ xs: 'column', md: 'row' }}
                        justifyContent="space-between"
                        spacing={1.25}
                        alignItems={{ xs: 'stretch', md: 'center' }}
                      >
                        <Stack spacing={0.4}>
                          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                            <Typography sx={{ color: '#f8fafc', fontWeight: 900 }}>
                              {group.name}
                            </Typography>
                            <ActiveChip active={group.is_active} />
                            <Chip
                              size="small"
                              label={`${linkedLinesCount} ${
                                linkedLinesCount === 1 ? 'línea' : 'líneas'
                              }`}
                              variant="outlined"
                              sx={{
                                height: 22,
                                color: '#94a3b8',
                                borderColor: 'rgba(148,163,184,0.18)',
                                fontSize: '0.7rem',
                              }}
                            />
                          </Stack>

                          <Typography sx={{ color: '#94a3b8', fontSize: '0.9rem' }}>
                            {group.code}
                            {group.description ? ` · ${group.description}` : ''}
                          </Typography>
                        </Stack>

                        <Stack direction="row" spacing={1} alignItems="center">
                          <Button
                            size="small"
                            startIcon={<EditRoundedIcon />}
                            onClick={() => openEditLineGroup(group)}
                          >
                            Editar
                          </Button>

                          <Button
                            size="small"
                            color="warning"
                            startIcon={<DeleteOutlineRoundedIcon />}
                            disabled={!group.is_active}
                            onClick={() => deactivateLineGroupMutation.mutate(group.id)}
                          >
                            Desactivar
                          </Button>

                          <Stack
                            justifyContent="center"
                            alignItems="center"
                            sx={{
                              width: 38,
                              minHeight: 34,
                              borderRadius: 2,
                              border: '1px dashed rgba(148,163,184,0.16)',
                              bgcolor: 'rgba(15,23,42,0.22)',
                            }}
                          >
                            <DragIndicatorRoundedIcon
                              sx={{ color: '#94a3b8', fontSize: '1.05rem' }}
                            />
                          </Stack>
                        </Stack>
                      </Stack>
                    </Paper>
                  );
                })}
              </Stack>
            )}
          </Stack>
        </Paper>

        {(linesQuery.isFetching ||
          lineGroupsQuery.isFetching ||
          reorderLinesMutation.isPending ||
          reorderLineGroupsMutation.isPending ||
          saveLineGroupMutation.isPending ||
          deactivateLineGroupMutation.isPending) && <LinearProgress />}

        <Stack spacing={1}>
          {lines.length === 0 ? (
            <Alert severity="info" variant="outlined">
              No hay líneas configuradas.
            </Alert>
          ) : (
            lineProcessGroups.map((group) => (
              <Stack key={group.key} spacing={1.1}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography
                    sx={{
                      color: '#38bdf8',
                      fontWeight: 950,
                      fontSize: '0.82rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.08em',
                    }}
                  >
                    {group.label}
                  </Typography>
                  <Chip
                    size="small"
                    label={`${group.lines.length} ${group.lines.length === 1 ? 'línea' : 'líneas'}`}
                    variant="outlined"
                    sx={{
                      height: 22,
                      color: '#94a3b8',
                      borderColor: 'rgba(148,163,184,0.18)',
                      fontSize: '0.7rem',
                    }}
                  />
                </Stack>

                <Stack spacing={1}>
                  {group.lines.map((line) => (
                    <Paper
                      key={line.id}
                      elevation={0}
                      draggable
                      onDragStart={() => setDraggedProductionLineId(line.id)}
                      onDragOver={(event) => {
                        event.preventDefault();
                      }}
                      onDrop={() => {
                        if (draggedProductionLineId) {
                          reorderProductionLine(draggedProductionLineId, line.id);
                        }
                        setDraggedProductionLineId(null);
                      }}
                      onDragEnd={() => setDraggedProductionLineId(null)}
                      sx={{
                        borderRadius: 3,
                        border:
                          draggedProductionLineId === line.id
                            ? '1px solid rgba(56,189,248,0.55)'
                            : '1px solid rgba(148,163,184,0.14)',
                        bgcolor:
                          draggedProductionLineId === line.id
                            ? 'rgba(8,47,73,0.24)'
                            : 'rgba(2,6,23,0.28)',
                        px: 1.5,
                        py: 1.25,
                        cursor: 'grab',
                      }}
                    >
                      <Stack
                        direction={{ xs: 'column', md: 'row' }}
                        justifyContent="space-between"
                        spacing={1.25}
                        alignItems={{ xs: 'stretch', md: 'center' }}
                      >
                        <Stack spacing={0.4}>
                          <Typography sx={{ color: '#f8fafc', fontWeight: 900 }}>
                            {line.name}
                          </Typography>
                          <Typography sx={{ color: '#94a3b8', fontSize: '0.9rem' }}>
                            {line.code} · {line.description || 'Sin descripción'}
                          </Typography>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <ActiveChip active={line.is_active} />
                            <Typography sx={{ color: '#64748b', fontSize: '0.82rem' }}>
                              Posición {(lines.findIndex((item) => item.id === line.id) + 1)}
                            </Typography>
                          </Stack>
                        </Stack>

                        <Stack direction="row" spacing={1} alignItems="center">
                          <Button
                            size="small"
                            startIcon={<EditRoundedIcon />}
                            onClick={() => openEditLine(line)}
                          >
                            Editar
                          </Button>

                          <Button
                            size="small"
                            color="warning"
                            startIcon={<DeleteOutlineRoundedIcon />}
                            disabled={!line.is_active}
                            onClick={() => deactivateLineMutation.mutate(line.id)}
                          >
                            Desactivar
                          </Button>

                          <Stack
                            justifyContent="center"
                            alignItems="center"
                            sx={{
                              width: 38,
                              minHeight: 34,
                              borderRadius: 2,
                              border: '1px dashed rgba(148,163,184,0.16)',
                              bgcolor: 'rgba(15,23,42,0.22)',
                            }}
                          >
                            <DragIndicatorRoundedIcon
                              sx={{ color: '#94a3b8', fontSize: '1.05rem' }}
                            />
                          </Stack>
                        </Stack>
                      </Stack>
                    </Paper>
                  ))}
                </Stack>
              </Stack>
            ))
          )}
        </Stack>
      </Stack>
    </Paper>
  );

  const renderShifts = () => (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 4,
        border: '1px solid rgba(148,163,184,0.18)',
        bgcolor: 'rgba(15,23,42,0.66)',
        p: { xs: 2.5, md: 3 },
      }}
    >
      <Stack spacing={2}>
        <SectionHeader
          title="Turnos"
          description="Define cuántos turnos existen y sus horarios. Esto alimentará el turno actual automático."
          actionLabel="Nuevo turno"
          onAction={openCreateShift}
        />

        {shiftsQuery.isFetching && <LinearProgress />}

        {renderRows(
          shifts,
          (shift) => (
            <>
              <Typography sx={{ color: '#f8fafc', fontWeight: 900 }}>
                {shift.label}
              </Typography>
              <Typography sx={{ color: '#94a3b8', fontSize: '0.9rem' }}>
                {shift.code} · {shift.start_time.slice(0, 5)}–{shift.end_time.slice(0, 5)}
              </Typography>
            </>
          ),
          openEditShift,
          (id) => deactivateShiftMutation.mutate(id),
        )}
      </Stack>
    </Paper>
  );

  const renderStopReasons = () => (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 4,
        border: '1px solid rgba(148,163,184,0.18)',
        bgcolor: 'rgba(15,23,42,0.66)',
        p: { xs: 2.5, md: 3 },
      }}
    >
      <Stack spacing={2}>
        <SectionHeader
          title="Motivos de parada"
          description="Define las causas rápidas disponibles al registrar paradas o incidencias."
          actionLabel="Nuevo motivo"
          onAction={openCreateStopReason}
        />

        {stopReasonsQuery.isFetching && <LinearProgress />}

        {renderRows(
          stopReasons,
          (reason) => (
            <>
              <Typography sx={{ color: '#f8fafc', fontWeight: 900 }}>
                {reason.label}
              </Typography>
              <Typography sx={{ color: '#94a3b8', fontSize: '0.9rem' }}>
                {reason.code}
              </Typography>
            </>
          ),
          openEditStopReason,
          (id) => deactivateStopReasonMutation.mutate(id),
        )}
      </Stack>
    </Paper>
  );

  const renderQualityControls = () => (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 4,
        border: '1px solid rgba(148,163,184,0.18)',
        bgcolor: 'rgba(15,23,42,0.66)',
        p: { xs: 2.5, md: 3 },
      }}
    >
      <Stack spacing={2}>
        <SectionHeader
          title="Controles de calidad"
          description="Define los controles que se solicitarán durante la fabricación."
          actionLabel="Nuevo control"
          onAction={openCreateQualityControl}
        />

        {qualityControlsQuery.isFetching && <LinearProgress />}

        {renderRows(
          qualityControls,
          (control) => {
            const template = templateById.get(control.template_id);

            return (
              <>
                <Typography sx={{ color: '#f8fafc', fontWeight: 900 }}>
                  {control.label}
                </Typography>
                <Typography sx={{ color: '#94a3b8', fontSize: '0.9rem' }}>
                  {control.code} · {template?.name ?? 'Plantilla no encontrada'}
                </Typography>
                {control.description && (
                  <Typography sx={{ color: '#64748b', fontSize: '0.84rem' }}>
                    {control.description}
                  </Typography>
                )}
              </>
            );
          },
          openEditQualityControl,
          (id) => deactivateQualityControlMutation.mutate(id),
        )}
      </Stack>
    </Paper>
  );

  const renderTraceability = () => (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 4,
        border: '1px solid rgba(148,163,184,0.18)',
        bgcolor: 'rgba(2,6,23,0.55)',
        p: { xs: 2, md: 2.5 },
      }}
    >
      <Stack spacing={2.5}>
        <SectionHeader
          title="Trazabilidad"
          description="Configura el nivel global de trazabilidad de la planta. Las capacidades avanzadas preparan el camino hacia lotes controlados, genealogía, laboratorio, PLC, visión e IA."
          actionLabel="Guardar configuración"
          onAction={() => {
            if (operationForm) {
              saveOperationSettingsMutation.mutate(operationForm);
            }
          }}
        />

        {operationSettingsQuery.isLoading || !operationForm ? (
          <LinearProgress />
        ) : (
          <Stack spacing={2.5}>
            <Alert severity="info" variant="outlined">
              Esta configuración define la política global de trazabilidad de la planta. En esta
              fase no obliga a todas las empresas a trabajar como VALTALIA: el modo básico mantiene
              captura manual y reglas no restrictivas.
            </Alert>

            <Paper
              elevation={0}
              sx={{
                borderRadius: 3,
                border: '1px solid rgba(148,163,184,0.14)',
                bgcolor: 'rgba(15,23,42,0.5)',
                p: 2,
              }}
            >
              <Stack spacing={2}>
                <Stack spacing={0.5}>
                  <Typography sx={{ color: '#f8fafc', fontWeight: 900 }}>
                    Nivel de trazabilidad
                  </Typography>
                  <Typography sx={{ color: '#94a3b8', fontSize: '0.9rem' }}>
                    Elige la política base. Los niveles superiores se irán activando por fases
                    técnicas sin romper plantas simples.
                  </Typography>
                </Stack>

                <TextField
                  select
                  size="small"
                  label="Modo"
                  value={operationForm.traceability_config.mode}
                  onChange={(event) =>
                    updateTraceabilityConfig({
                      mode: event.target
                        .value as UpdateProductionOperationSettingsPayload['traceability_config']['mode'],
                    })
                  }
                  fullWidth
                >
                  <MenuItem value="none">Nivel 0 · Sin trazabilidad</MenuItem>
                  <MenuItem value="basic_manual">Nivel 1 · Básica manual</MenuItem>
                  <MenuItem value="controlled_lots">Nivel 2 · Lotes controlados</MenuItem>
                  <MenuItem value="regulated_genealogy">
                    Nivel 3 · Genealogía regulada
                  </MenuItem>
                  <MenuItem value="process_ai">Nivel 4 · Proceso + IA</MenuItem>
                </TextField>
              </Stack>
            </Paper>

            <Paper
              elevation={0}
              sx={{
                borderRadius: 3,
                border: '1px solid rgba(148,163,184,0.14)',
                bgcolor: 'rgba(15,23,42,0.5)',
                p: 2,
              }}
            >
              <Stack spacing={2}>
                <Stack spacing={0.5}>
                  <Typography sx={{ color: '#f8fafc', fontWeight: 900 }}>
                    Materiales y lotes
                  </Typography>
                  <Typography sx={{ color: '#94a3b8', fontSize: '0.9rem' }}>
                    Reglas de captura manual de materiales/lotes asociadas a líneas y segmentos.
                  </Typography>
                </Stack>

                <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Switch
                      checked={
                        operationForm.traceability_config.material_usage_capture_enabled
                      }
                      onChange={(event) =>
                        updateTraceabilityConfig({
                          material_usage_capture_enabled: event.target.checked,
                        })
                      }
                    />
                    <Typography sx={{ color: '#e2e8f0' }}>
                      Capturar materiales/lotes
                    </Typography>
                  </Stack>

                  <Stack direction="row" spacing={1} alignItems="center">
                    <Switch
                      checked={
                        operationForm.traceability_config.require_material_usage_on_line_close
                      }
                      onChange={(event) =>
                        updateTraceabilityConfig({
                          require_material_usage_on_line_close: event.target.checked,
                        })
                      }
                    />
                    <Typography sx={{ color: '#e2e8f0' }}>
                      Exigir material al cerrar línea
                    </Typography>
                  </Stack>

                  <Stack direction="row" spacing={1} alignItems="center">
                    <Switch
                      checked={operationForm.traceability_config.require_lot_on_material_usage}
                      onChange={(event) =>
                        updateTraceabilityConfig({
                          require_lot_on_material_usage: event.target.checked,
                        })
                      }
                    />
                    <Typography sx={{ color: '#e2e8f0' }}>
                      Exigir lote en cada material
                    </Typography>
                  </Stack>

                  <Stack direction="row" spacing={1} alignItems="center">
                    <Switch
                      checked={
                        operationForm.traceability_config.require_quantity_on_material_usage
                      }
                      onChange={(event) =>
                        updateTraceabilityConfig({
                          require_quantity_on_material_usage: event.target.checked,
                        })
                      }
                    />
                    <Typography sx={{ color: '#e2e8f0' }}>
                      Exigir cantidad consumida
                    </Typography>
                  </Stack>

                  <Stack direction="row" spacing={1} alignItems="center">
                    <Switch
                      checked={operationForm.traceability_config.require_existing_material_lot}
                      onChange={(event) =>
                        updateTraceabilityConfig({
                          require_existing_material_lot: event.target.checked,
                        })
                      }
                    />
                    <Typography sx={{ color: '#e2e8f0' }}>
                      Exigir lote existente en catálogo
                    </Typography>
                  </Stack>

                  <Stack direction="row" spacing={1} alignItems="center">
                    <Switch
                      checked={operationForm.traceability_config.allow_manual_material_name}
                      onChange={(event) =>
                        updateTraceabilityConfig({
                          allow_manual_material_name: event.target.checked,
                        })
                      }
                    />
                    <Typography sx={{ color: '#e2e8f0' }}>
                      Permitir material manual
                    </Typography>
                  </Stack>
                </Stack>

                <Alert severity="warning" variant="outlined">
                  Las reglas de obligatoriedad se guardan aquí, pero el bloqueo operativo de cierre
                  se conectará en una tanda posterior. Hasta entonces no cambia el comportamiento del
                  Puesto Operario.
                </Alert>
              </Stack>
            </Paper>

            <Paper
              elevation={0}
              sx={{
                borderRadius: 3,
                border: '1px solid rgba(148,163,184,0.14)',
                bgcolor: 'rgba(15,23,42,0.5)',
                p: 2,
              }}
            >
              <Stack spacing={2}>
                <Stack
                  direction={{ xs: 'column', md: 'row' }}
                  spacing={1.5}
                  alignItems={{ xs: 'stretch', md: 'center' }}
                  justifyContent="space-between"
                >
                  <Stack spacing={0.5}>
                    <Typography sx={{ color: '#f8fafc', fontWeight: 900 }}>
                      Lotes controlados de material
                    </Typography>
                    <Typography sx={{ color: '#94a3b8', fontSize: '0.9rem' }}>
                      Catálogo mínimo de lotes disponibles para trazabilidad de Nivel 2.
                      Estos lotes pueden seleccionarse después desde Puesto Operario.
                    </Typography>
                  </Stack>

                  {materialLotForm.id && (
                    <Button
                      variant="outlined"
                      onClick={() => setMaterialLotForm(emptyMaterialLotForm)}
                    >
                      Nuevo lote
                    </Button>
                  )}
                </Stack>

                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                      xs: '1fr',
                      md: 'repeat(3, minmax(0, 1fr))',
                    },
                    gap: 1.25,
                  }}
                >
                  <TextField
                    size="small"
                    label="Material *"
                    value={materialLotForm.material_name}
                    onChange={(event) =>
                      setMaterialLotForm((current) => ({
                        ...current,
                        material_name: event.target.value,
                      }))
                    }
                    fullWidth
                  />

                  <TextField
                    size="small"
                    label="Lote *"
                    value={materialLotForm.lot_number}
                    onChange={(event) =>
                      setMaterialLotForm((current) => ({
                        ...current,
                        lot_number: event.target.value,
                      }))
                    }
                    fullWidth
                  />

                  <TextField
                    size="small"
                    label="Proveedor"
                    value={materialLotForm.supplier_name}
                    onChange={(event) =>
                      setMaterialLotForm((current) => ({
                        ...current,
                        supplier_name: event.target.value,
                      }))
                    }
                    fullWidth
                  />

                  <TextField
                    size="small"
                    label="Lote proveedor"
                    value={materialLotForm.supplier_lot_number}
                    onChange={(event) =>
                      setMaterialLotForm((current) => ({
                        ...current,
                        supplier_lot_number: event.target.value,
                      }))
                    }
                    fullWidth
                  />

                  <TextField
                    size="small"
                    type="date"
                    label="Fecha recepción"
                    value={materialLotForm.received_at}
                    onChange={(event) =>
                      setMaterialLotForm((current) => ({
                        ...current,
                        received_at: event.target.value,
                      }))
                    }
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                  />

                  <TextField
                    size="small"
                    type="date"
                    label="Caducidad"
                    value={materialLotForm.expiration_date}
                    onChange={(event) =>
                      setMaterialLotForm((current) => ({
                        ...current,
                        expiration_date: event.target.value,
                      }))
                    }
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                  />

                  <TextField
                    size="small"
                    type="number"
                    label="Cantidad recibida"
                    value={materialLotForm.quantity_received}
                    onChange={(event) =>
                      setMaterialLotForm((current) => ({
                        ...current,
                        quantity_received: event.target.value,
                      }))
                    }
                    fullWidth
                  />

                  <TextField
                    size="small"
                    type="number"
                    label="Cantidad disponible"
                    value={materialLotForm.quantity_available}
                    onChange={(event) =>
                      setMaterialLotForm((current) => ({
                        ...current,
                        quantity_available: event.target.value,
                      }))
                    }
                    helperText="Si se deja vacía al crear, backend la iguala a la recibida."
                    fullWidth
                  />

                  <TextField
                    size="small"
                    label="Unidad"
                    value={materialLotForm.quantity_unit}
                    onChange={(event) =>
                      setMaterialLotForm((current) => ({
                        ...current,
                        quantity_unit: event.target.value,
                      }))
                    }
                    fullWidth
                  />

                  <TextField
                    select
                    size="small"
                    label="Estado"
                    value={materialLotForm.status}
                    onChange={(event) =>
                      setMaterialLotForm((current) => ({
                        ...current,
                        status: event.target.value as ProductionMaterialLotStatus,
                      }))
                    }
                    fullWidth
                  >
                    <MenuItem value="available">Disponible</MenuItem>
                    <MenuItem value="quarantined">Cuarentena</MenuItem>
                    <MenuItem value="blocked">Bloqueado</MenuItem>
                    <MenuItem value="consumed">Consumido</MenuItem>
                    <MenuItem value="expired">Caducado</MenuItem>
                    <MenuItem value="voided">Anulado</MenuItem>
                  </TextField>

                  <TextField
                    select
                    size="small"
                    label="Estado calidad"
                    value={materialLotForm.quality_status}
                    onChange={(event) =>
                      setMaterialLotForm((current) => ({
                        ...current,
                        quality_status:
                          event.target.value as ProductionMaterialLotQualityStatus,
                      }))
                    }
                    fullWidth
                  >
                    <MenuItem value="pending">Pendiente</MenuItem>
                    <MenuItem value="accepted">Aceptado</MenuItem>
                    <MenuItem value="rejected">Rechazado</MenuItem>
                    <MenuItem value="conditional">Condicional</MenuItem>
                    <MenuItem value="not_required">No requerido</MenuItem>
                  </TextField>

                  <TextField
                    size="small"
                    label="Notas"
                    value={materialLotForm.notes}
                    onChange={(event) =>
                      setMaterialLotForm((current) => ({
                        ...current,
                        notes: event.target.value,
                      }))
                    }
                    fullWidth
                    sx={{
                      gridColumn: {
                        xs: 'auto',
                        md: '1 / -1',
                      },
                    }}
                  />
                </Box>

                <Stack direction="row" spacing={1} justifyContent="flex-end">
                  <Button
                    variant="outlined"
                    onClick={() => setMaterialLotForm(emptyMaterialLotForm)}
                  >
                    Limpiar
                  </Button>

                  <Button
                    variant="contained"
                    onClick={() =>
                      saveProductionMaterialLotMutation.mutate(materialLotForm)
                    }
                    disabled={saveProductionMaterialLotMutation.isPending}
                  >
                    {materialLotForm.id ? 'Actualizar lote' : 'Crear lote'}
                  </Button>
                </Stack>

                {productionMaterialLotsQuery.isFetching && <LinearProgress />}

                {productionMaterialLots.length === 0 ? (
                  <Alert severity="info" variant="outlined">
                    Todavía no hay lotes controlados. Puedes crear el primero desde este
                    formulario o importarlos más adelante desde ERP/inventario.
                  </Alert>
                ) : (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ color: '#94a3b8' }}>Material / lote</TableCell>
                          <TableCell sx={{ color: '#94a3b8' }}>Proveedor</TableCell>
                          <TableCell sx={{ color: '#94a3b8' }}>Disponible</TableCell>
                          <TableCell sx={{ color: '#94a3b8' }}>Estado</TableCell>
                          <TableCell sx={{ color: '#94a3b8' }}>Calidad</TableCell>
                          <TableCell align="right" sx={{ color: '#94a3b8' }}>
                            Acción
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {productionMaterialLots.map((lot) => (
                          <TableRow key={lot.id}>
                            <TableCell>
                              <Stack spacing={0.25}>
                                <Typography sx={{ color: '#f8fafc', fontWeight: 800 }}>
                                  {lot.material_name}
                                </Typography>
                                <Typography sx={{ color: '#94a3b8', fontSize: '0.84rem' }}>
                                  {lot.lot_number}
                                </Typography>
                              </Stack>
                            </TableCell>
                            <TableCell sx={{ color: '#cbd5e1' }}>
                              {lot.supplier_name || '—'}
                            </TableCell>
                            <TableCell sx={{ color: '#cbd5e1' }}>
                              {formatMaterialLotQuantity(lot)}
                            </TableCell>
                            <TableCell>
                              <Chip
                                size="small"
                                variant="outlined"
                                label={lot.status}
                                sx={{ color: '#e2e8f0' }}
                              />
                            </TableCell>
                            <TableCell>
                              <Chip
                                size="small"
                                variant="outlined"
                                label={lot.quality_status}
                                sx={{ color: '#e2e8f0' }}
                              />
                            </TableCell>
                            <TableCell align="right">
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={<EditRoundedIcon />}
                                onClick={() =>
                                  setMaterialLotForm(buildMaterialLotFormFromLot(lot))
                                }
                              >
                                Editar
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Stack>
            </Paper>

            <Paper
              elevation={0}
              sx={{
                borderRadius: 3,
                border: '1px solid rgba(148,163,184,0.14)',
                bgcolor: 'rgba(15,23,42,0.5)',
                p: 2,
              }}
            >
              <Stack spacing={2}>
                <Stack spacing={0.5}>
                  <Typography sx={{ color: '#f8fafc', fontWeight: 900 }}>
                    Capacidades avanzadas preparadas
                  </Typography>
                  <Typography sx={{ color: '#94a3b8', fontSize: '0.9rem' }}>
                    Estado funcional previsto para fases VALTALIA / alimentación regulada /
                    proceso + IA. En esta pantalla se muestran como capacidades de arquitectura, no
                    como módulos plenamente activos todavía.
                  </Typography>
                </Stack>

                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {[
                    {
                      label: 'Segmentos por línea',
                      enabled: operationForm.traceability_config.enable_run_segments,
                    },
                    {
                      label: 'Tramos manuales',
                      enabled: operationForm.traceability_config.enable_manual_run_segments,
                    },
                    {
                      label: 'Fórmulas reales',
                      enabled: operationForm.traceability_config.enable_formula_tracking,
                    },
                    {
                      label: 'Lotes de salida',
                      enabled: operationForm.traceability_config.enable_output_lots,
                    },
                    {
                      label: 'Muestras laboratorio',
                      enabled: operationForm.traceability_config.enable_quality_samples,
                    },
                    {
                      label: 'Variables PLC',
                      enabled: operationForm.traceability_config.enable_process_variables,
                    },
                    {
                      label: 'Defectos visión',
                      enabled: operationForm.traceability_config.enable_vision_defects,
                    },
                    {
                      label: 'Recall / genealogía',
                      enabled: operationForm.traceability_config.enable_recall_traceability,
                    },
                  ].map((item) => (
                    <Chip
                      key={item.label}
                      label={`${item.label}: ${item.enabled ? 'preparado' : 'pendiente'}`}
                      variant="outlined"
                      sx={{
                        color: item.enabled ? '#67e8f9' : '#94a3b8',
                        borderColor: item.enabled
                          ? 'rgba(103,232,249,0.38)'
                          : 'rgba(148,163,184,0.22)',
                        fontWeight: 800,
                      }}
                    />
                  ))}
                </Stack>
              </Stack>
            </Paper>
          </Stack>
        )}
      </Stack>
    </Paper>
  );

  return (
    <Stack spacing={3}>
      <Paper
        elevation={0}
        sx={{
          borderRadius: 4,
          border: '1px solid rgba(148,163,184,0.18)',
          bgcolor: 'rgba(15,23,42,0.72)',
          px: { xs: 2.5, md: 3 },
          py: { xs: 2.5, md: 3 },
        }}
      >
        <Stack spacing={1.5}>
          <Stack direction="row" spacing={1.25} alignItems="center">
            <SettingsRoundedIcon sx={{ color: '#38bdf8' }} />
            <Typography sx={{ color: '#f8fafc', fontSize: '1.55rem', fontWeight: 950 }}>
              Configuración de planta
            </Typography>
          </Stack>

          <Typography sx={{ color: '#94a3b8', maxWidth: 900 }}>
            Configura líneas, turnos, motivos de parada, controles de calidad, trazabilidad y reglas operativas usadas por el puesto operario y el control de fábrica.
          </Typography>

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {[
              { key: 'lines', label: 'Líneas / puestos' },
              { key: 'shifts', label: 'Turnos' },
              { key: 'stop-reasons', label: 'Motivos de parada' },
              { key: 'quality-controls', label: 'Controles de calidad' },
              { key: 'traceability', label: 'Trazabilidad' },
              { key: 'operation', label: 'Operación' },
            ].map((item) => (
              <Button
                key={item.key}
                variant={tab === item.key ? 'contained' : 'outlined'}
                onClick={() => setTab(item.key as ConfigTab)}
              >
                {item.label}
              </Button>
            ))}
          </Stack>
        </Stack>
      </Paper>

      {tab === 'lines' && renderLines()}
      {tab === 'shifts' && renderShifts()}
      {tab === 'stop-reasons' && renderStopReasons()}
      {tab === 'quality-controls' && renderQualityControls()}
      {tab === 'traceability' && renderTraceability()}

      {tab === 'operation' && (
        <Paper
          elevation={0}
          sx={{
            borderRadius: 4,
            border: '1px solid rgba(148,163,184,0.18)',
            bgcolor: 'rgba(2,6,23,0.55)',
            p: { xs: 2, md: 2.5 },
          }}
        >
          <Stack spacing={2.5}>
            <SectionHeader
              title="Operación"
              description="Configura campos de la orden, parámetros de producción, estimaciones y reglas básicas del flujo."
              actionLabel="Guardar configuración"
              onAction={() => {
                if (operationForm) {
                  saveOperationSettingsMutation.mutate(operationForm);
                }
              }}
            />

            {operationSettingsQuery.isLoading || !operationForm ? (
              <LinearProgress />
            ) : (
              <Stack spacing={3}>
                <Paper
                  elevation={0}
                  sx={{
                    borderRadius: 3,
                    border: '1px solid rgba(148,163,184,0.14)',
                    bgcolor: 'rgba(15,23,42,0.5)',
                    p: 2,
                  }}
                >
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={2}
                    alignItems={{ xs: 'stretch', md: 'center' }}
                    justifyContent="space-between"
                  >
                    <Stack spacing={0.5}>
                      <Typography sx={{ color: '#f8fafc', fontWeight: 900 }}>
                        Importación de OFs
                      </Typography>
                      <Typography sx={{ color: '#94a3b8', fontSize: '0.9rem' }}>
                        Sube el archivo diario de OFs para precargar órdenes no iniciadas. Solo
                        son obligatorios el número de OF y la cantidad total.
                      </Typography>

                      {importedManufacturingOrdersFile && (
                        <Typography sx={{ color: '#cbd5e1', fontSize: '0.82rem' }}>
                          Archivo preparado: {importedManufacturingOrdersFile.name}
                        </Typography>
                      )}

                      {importedManufacturingOrdersConfirmResult && (
                        <Typography sx={{ color: '#86efac', fontSize: '0.82rem' }}>
                          Última importación: creadas{' '}
                          {importedManufacturingOrdersConfirmResult.rows_created}, actualizadas{' '}
                          {importedManufacturingOrdersConfirmResult.rows_updated}, importadas{' '}
                          {importedManufacturingOrdersConfirmResult.rows_imported}.
                        </Typography>
                      )}
                    </Stack>

                    {!canImport ? (
                      <Typography sx={{ color: '#fbbf24', fontSize: 12 }}>
                        {IMPORT_PERMISSION_DENIED_TEXT}
                      </Typography>
                    ) : null}
                    <Button
                      variant="contained"
                      component="label"
                      disabled={!canImport}
                    >
                      Importar archivo
                      <input
                        hidden
                        type="file"
                        accept=".csv,.xlsx"
                        onChange={(event) => {
                          const nextFile = event.target.files?.[0] ?? null;

                          event.target.value = '';

                          setImportedManufacturingOrdersFile(nextFile);
                          setImportedManufacturingOrdersPreview(null);
                          setImportedManufacturingOrdersConfirmResult(null);
                          setImportedManufacturingOrdersNormalizationResult(null);
                          setImportedManufacturingOrdersNormalizationReviewOpen(false);
                          setImportedManufacturingOrdersNormalizationReviewSheetName('');
                          setImportedManufacturingOrdersNormalizationReviewHeaderRowInput('1');
                          setImportedManufacturingOrdersPlanningMappingOpen(false);

                          if (nextFile) {
                            setImportedManufacturingOrdersImportDialogOpen(true);
                            previewImportedManufacturingOrdersImportMutation.mutate({
                              file: nextFile,
                              mode: 'auto',
                            });
                          }
                        }}
                      />
                    </Button>
                  </Stack>
                </Paper>

                <Dialog
                  open={importedManufacturingOrdersImportDialogOpen}
                  onClose={() => {
                    if (
                      previewImportedManufacturingOrdersImportMutation.isPending ||
                      confirmImportedManufacturingOrdersImportMutation.isPending
                    ) {
                      return;
                    }

                    setImportedManufacturingOrdersImportDialogOpen(false);
                  }}
                  fullWidth
                  maxWidth="lg"
                >
                  <DialogTitle>Importar pedidos pendientes de fabricar</DialogTitle>

                  <DialogContent dividers>
                    <Stack spacing={2}>
                      <Alert severity="info" variant="outlined">
                        Sube el archivo diario de OFs. El sistema detectará automáticamente la hoja
                        y la fila de cabecera antes de generar la previsualización.
                      </Alert>

                      <TextField
                        size="small"
                        fullWidth
                        label="Archivo seleccionado"
                        value={importedManufacturingOrdersFile?.name ?? ''}
                        InputProps={{ readOnly: true }}
                      />

                      {importedManufacturingOrdersNormalizationResult && (
                        <Stack spacing={1.5}>
                          <Alert
                            severity={
                              importedManufacturingOrdersNormalizationResult.warnings.length > 0
                                ? 'warning'
                                : 'info'
                            }
                            action={
                              <Button
                                color="inherit"
                                size="small"
                                variant="outlined"
                                onClick={() => {
                                  setImportedManufacturingOrdersNormalizationReviewSheetName(
                                    importedManufacturingOrdersNormalizationResult.selected_sheet ??
                                      '',
                                  );
                                  setImportedManufacturingOrdersNormalizationReviewHeaderRowInput(
                                    String(
                                      importedManufacturingOrdersNormalizationResult.header_row_index +
                                        1,
                                    ),
                                  );
                                  setImportedManufacturingOrdersNormalizationReviewOpen(true);
                                }}
                                disabled={
                                  previewImportedManufacturingOrdersImportMutation.isPending ||
                                  confirmImportedManufacturingOrdersImportMutation.isPending
                                }
                              >
                                Revisar normalización
                              </Button>
                            }
                          >
                            <Stack spacing={0.75}>
                              <Typography sx={{ fontWeight: 800 }}>
                                {importedManufacturingOrdersNormalizationResult.source === 'profile'
                                  ? 'Configuración recordada aplicada'
                                  : importedManufacturingOrdersNormalizationResult.source ===
                                      'manual_review'
                                    ? 'Configuración revisada manualmente'
                                    : 'Detección automática aplicada'}
                              </Typography>

                              <Typography variant="body2">
                                {importedManufacturingOrdersNormalizationResult.selected_sheet
                                  ? `Hoja: ${importedManufacturingOrdersNormalizationResult.selected_sheet} · `
                                  : ''}
                                Cabecera: fila{' '}
                                {importedManufacturingOrdersNormalizationResult.header_row_index + 1}
                              </Typography>

                              {importedManufacturingOrdersNormalizationResult.warnings.map(
                                (warning, index) => (
                                  <Typography
                                    key={`${warning.code}-${index}`}
                                    variant="body2"
                                  >
                                    {warning.message}
                                  </Typography>
                                ),
                              )}
                            </Stack>
                          </Alert>

                          {importedManufacturingOrdersNormalizationReviewOpen && (
                            <Stack
                              spacing={1.5}
                              sx={{
                                p: 1.5,
                                border: '1px solid rgba(148,163,184,0.16)',
                                borderRadius: 2,
                                bgcolor: 'rgba(2,6,23,0.24)',
                              }}
                            >
                              <Typography sx={{ color: '#f8fafc', fontWeight: 850 }}>
                                Revisar normalización
                              </Typography>

                              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
                                <TextField
                                  select
                                  fullWidth
                                  size="small"
                                  label="Hoja"
                                  value={importedManufacturingOrdersNormalizationReviewSheetName}
                                  onChange={(event) =>
                                    setImportedManufacturingOrdersNormalizationReviewSheetName(
                                      event.target.value,
                                    )
                                  }
                                >
                                  {(
                                    importedManufacturingOrdersNormalizationResult.sheets ?? []
                                  ).map((sheet) => (
                                    <MenuItem key={sheet.name} value={sheet.name}>
                                      {sheet.name}
                                    </MenuItem>
                                  ))}
                                </TextField>

                                <TextField
                                  fullWidth
                                  size="small"
                                  label="Fila de cabecera"
                                  type="number"
                                  value={
                                    importedManufacturingOrdersNormalizationReviewHeaderRowInput
                                  }
                                  onChange={(event) =>
                                    setImportedManufacturingOrdersNormalizationReviewHeaderRowInput(
                                      event.target.value,
                                    )
                                  }
                                  inputProps={{ min: 1 }}
                                />
                              </Stack>

                              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
                                <Button
                                  variant="outlined"
                                  onClick={() => {
                                    setImportedManufacturingOrdersNormalizationReviewOpen(false);
                                    setImportedManufacturingOrdersNormalizationReviewSheetName(
                                      importedManufacturingOrdersNormalizationResult.selected_sheet ??
                                        '',
                                    );
                                    setImportedManufacturingOrdersNormalizationReviewHeaderRowInput(
                                      String(
                                        importedManufacturingOrdersNormalizationResult.header_row_index +
                                          1,
                                      ),
                                    );
                                  }}
                                  disabled={
                                    previewImportedManufacturingOrdersImportMutation.isPending ||
                                    confirmImportedManufacturingOrdersImportMutation.isPending
                                  }
                                >
                                  Cancelar revisión
                                </Button>

                                <Button
                                  variant="contained"
                                  onClick={() => {
                                    if (!importedManufacturingOrdersFile) {
                                      return;
                                    }

                                    const parsedHeaderRow = Number(
                                      importedManufacturingOrdersNormalizationReviewHeaderRowInput,
                                    );

                                    if (
                                      !Number.isInteger(parsedHeaderRow) ||
                                      parsedHeaderRow < 1
                                    ) {
                                      enqueueSnackbar(
                                        'La fila de cabecera debe ser un número entero mayor o igual que 1.',
                                        { variant: 'error' },
                                      );
                                      return;
                                    }

                                    previewImportedManufacturingOrdersImportMutation.mutate({
                                      file: importedManufacturingOrdersFile,
                                      mode: 'manual_review',
                                      sheetName:
                                        importedManufacturingOrdersNormalizationReviewSheetName ||
                                        null,
                                      headerRowIndex: parsedHeaderRow - 1,
                                    });
                                  }}
                                  disabled={
                                    !importedManufacturingOrdersFile ||
                                    previewImportedManufacturingOrdersImportMutation.isPending ||
                                    confirmImportedManufacturingOrdersImportMutation.isPending
                                  }
                                >
                                  Aplicar revisión
                                </Button>
                              </Stack>

                              <Stack spacing={1}>
                                <Typography sx={{ color: '#f8fafc', fontWeight: 800 }}>
                                  Vista previa cruda de la hoja
                                </Typography>

                                <Typography sx={{ color: '#94a3b8', fontSize: '0.82rem' }}>
                                  Revisa las primeras filas detectadas y confirma que la fila marcada
                                  como cabecera coincide con los nombres reales de columnas.
                                </Typography>

                                <Stack
                                  spacing={0.75}
                                  sx={{
                                    border: '1px solid rgba(148,163,184,0.14)',
                                    borderRadius: 1.5,
                                    overflow: 'hidden',
                                    bgcolor: 'rgba(2,6,23,0.22)',
                                  }}
                                >
                                  {(
                                    importedManufacturingOrdersNormalizationResult.raw_preview_rows ??
                                    []
                                  )
                                    .slice(0, 8)
                                    .map((row, rowIndex) => {
                                      const isSelectedHeaderRow =
                                        rowIndex + 1 ===
                                        Number(
                                          importedManufacturingOrdersNormalizationReviewHeaderRowInput,
                                        );

                                      return (
                                        <Stack
                                          key={`imported-manufacturing-orders-raw-row-${rowIndex}`}
                                          direction="row"
                                          spacing={1}
                                          alignItems="stretch"
                                          sx={{
                                            px: 1.25,
                                            py: 1,
                                            borderTop:
                                              rowIndex === 0
                                                ? 'none'
                                                : '1px solid rgba(148,163,184,0.08)',
                                            bgcolor: isSelectedHeaderRow
                                              ? 'rgba(34,211,238,0.12)'
                                              : 'transparent',
                                          }}
                                        >
                                          <Typography
                                            sx={{
                                              minWidth: 28,
                                              color: isSelectedHeaderRow
                                                ? '#67e8f9'
                                                : '#94a3b8',
                                              fontWeight: 800,
                                              fontSize: '0.82rem',
                                              pt: 0.125,
                                            }}
                                          >
                                            {rowIndex + 1}
                                          </Typography>

                                          <Stack
                                            direction={{ xs: 'column', md: 'row' }}
                                            spacing={1}
                                            useFlexGap
                                            flexWrap="wrap"
                                            sx={{ minWidth: 0 }}
                                          >
                                            {row.slice(0, 5).map((cell, cellIndex) => (
                                              <Typography
                                                key={`imported-manufacturing-orders-raw-cell-${rowIndex}-${cellIndex}`}
                                                sx={{
                                                  px: 0.75,
                                                  py: 0.35,
                                                  borderRadius: 1,
                                                  bgcolor: 'rgba(15,23,42,0.45)',
                                                  color: '#e2e8f0',
                                                  fontSize: '0.82rem',
                                                  fontWeight: isSelectedHeaderRow ? 800 : 500,
                                                  wordBreak: 'break-word',
                                                }}
                                              >
                                                {cell === null ||
                                                cell === undefined ||
                                                String(cell).trim() === ''
                                                  ? '—'
                                                  : String(cell)}
                                              </Typography>
                                            ))}

                                            {row.length > 5 && (
                                              <Typography
                                                sx={{
                                                  px: 0.75,
                                                  py: 0.35,
                                                  borderRadius: 1,
                                                  bgcolor: 'rgba(15,23,42,0.28)',
                                                  color: '#94a3b8',
                                                  fontSize: '0.8rem',
                                                  fontStyle: 'italic',
                                                }}
                                              >
                                                +{row.length - 5} columnas más
                                              </Typography>
                                            )}
                                          </Stack>
                                        </Stack>
                                      );
                                    })}

                                  {(importedManufacturingOrdersNormalizationResult.raw_preview_rows ??
                                    []).length === 0 && (
                                    <Typography
                                      sx={{
                                        px: 1.25,
                                        py: 1.25,
                                        color: '#94a3b8',
                                        fontSize: '0.85rem',
                                      }}
                                    >
                                      No hay filas disponibles para mostrar en la vista previa cruda.
                                    </Typography>
                                  )}
                                </Stack>
                              </Stack>
                            </Stack>
                          )}
                        </Stack>
                      )}

                      {(previewImportedManufacturingOrdersImportMutation.isPending ||
                        confirmImportedManufacturingOrdersImportMutation.isPending) && (
                        <LinearProgress />
                      )}

                      {importedManufacturingOrdersPreview && (
                        <Paper
                          elevation={0}
                          sx={{
                            borderRadius: 2.5,
                            border: '1px solid rgba(148,163,184,0.12)',
                            bgcolor: 'rgba(2,6,23,0.28)',
                            p: 1.5,
                          }}
                        >
                          <Stack spacing={1.5}>
                            <Stack spacing={0.35}>
                              <Typography sx={{ color: '#f8fafc', fontWeight: 900 }}>
                                Mapeo de campos
                              </Typography>
                              <Typography sx={{ color: '#94a3b8', fontSize: '0.84rem' }}>
                                Revisa cómo se han enlazado las columnas detectadas con los campos
                                estándar de PROA. Corrige solo lo necesario.
                              </Typography>
                            </Stack>

                            <Paper
                              elevation={0}
                              sx={{
                                borderRadius: 2,
                                border: '1px solid rgba(148,163,184,0.12)',
                                bgcolor: 'rgba(15,23,42,0.34)',
                                p: 1.25,
                              }}
                            >
                              <Stack spacing={1}>
                                <Stack
                                  direction={{ xs: 'column', md: 'row' }}
                                  spacing={1}
                                  alignItems={{ xs: 'stretch', md: 'center' }}
                                  justifyContent="space-between"
                                >
                                  <Stack spacing={0.25} sx={{ minWidth: 0 }}>
                                    <Stack direction="row" spacing={0.75} alignItems="center">
                                      <Typography sx={{ color: '#f8fafc', fontWeight: 850 }}>
                                        Datos auxiliares del planificador
                                      </Typography>

                                      <Chip
                                        size="small"
                                        label="No operativo"
                                        variant="outlined"
                                        sx={{
                                          height: 22,
                                          color: '#94a3b8',
                                          borderColor: 'rgba(148,163,184,0.22)',
                                          fontSize: '0.7rem',
                                          fontWeight: 800,
                                        }}
                                      />
                                    </Stack>

                                    <Typography sx={{ color: '#94a3b8', fontSize: '0.8rem' }}>
                                      Duración orientativa importada solo para planificación. No se
                                      muestra en el puesto operario ni modifica el cálculo teórico real.
                                    </Typography>

                                    <Typography sx={{ color: '#64748b', fontSize: '0.76rem' }}>
                                      Selección actual:{' '}
                                      {importedManufacturingOrdersImportFlow.mapping[
                                        'planning_duration_source_value'
                                      ] ||
                                        importedManufacturingOrdersPreview.auto_mapping[
                                          'planning_duration_source_value'
                                        ] ||
                                        'sin columna'}{' '}
                                      ·{' '}
                                      {formatPlanningDurationUnitLabel(
                                        importedManufacturingOrdersImportFlow.mapping[
                                          'planning_duration_source_unit'
                                        ] ??
                                          importedManufacturingOrdersPreview.auto_mapping[
                                            'planning_duration_source_unit'
                                          ],
                                      )}
                                    </Typography>
                                  </Stack>

                                  <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={() =>
                                      setImportedManufacturingOrdersPlanningMappingOpen(
                                        (current) => !current,
                                      )
                                    }
                                  >
                                    {importedManufacturingOrdersPlanningMappingOpen
                                      ? 'Ocultar'
                                      : 'Configurar'}
                                  </Button>
                                </Stack>

                                <Collapse in={importedManufacturingOrdersPlanningMappingOpen}>
                                  <Stack spacing={1.25} sx={{ pt: 0.5 }}>
                                    <Stack
                                      direction={{ xs: 'column', md: 'row' }}
                                      spacing={1.5}
                                    >
                                      <TextField
                                        select
                                        fullWidth
                                        size="small"
                                        label="Columna duración orientativa"
                                        value={
                                          importedManufacturingOrdersImportFlow.mapping[
                                            'planning_duration_source_value'
                                          ] ??
                                          importedManufacturingOrdersPreview.auto_mapping[
                                            'planning_duration_source_value'
                                          ] ??
                                          ''
                                        }
                                        onChange={(event) => {
                                          const nextColumn = event.target.value;

                                          void (async () => {
                                            const nextFlow =
                                              await applyImportedManufacturingOrdersImportMapping(
                                                importedManufacturingOrdersImportFlow,
                                                'planning_duration_source_value',
                                                nextColumn,
                                              );

                                            setImportedManufacturingOrdersImportFlow(nextFlow);
                                          })();
                                        }}
                                        disabled={
                                          previewImportedManufacturingOrdersImportMutation.isPending ||
                                          confirmImportedManufacturingOrdersImportMutation.isPending
                                        }
                                      >
                                        <MenuItem value="">
                                          <em>Sin asignar</em>
                                        </MenuItem>

                                        {importedManufacturingOrdersPreview.columns.map((column) => (
                                          <MenuItem
                                            key={`planning-duration-column-${column}`}
                                            value={column}
                                          >
                                            {column}
                                          </MenuItem>
                                        ))}
                                      </TextField>

                                      <TextField
                                        select
                                        fullWidth
                                        size="small"
                                        label="Unidad duración"
                                        value={
                                          importedManufacturingOrdersImportFlow.mapping[
                                            'planning_duration_source_unit'
                                          ] ??
                                          importedManufacturingOrdersPreview.auto_mapping[
                                            'planning_duration_source_unit'
                                          ] ??
                                          ''
                                        }
                                        onChange={(event) => {
                                          const nextUnit = event.target.value;

                                          void (async () => {
                                            const nextFlow =
                                              await applyImportedManufacturingOrdersImportMapping(
                                                importedManufacturingOrdersImportFlow,
                                                'planning_duration_source_unit',
                                                nextUnit,
                                              );

                                            setImportedManufacturingOrdersImportFlow(nextFlow);
                                          })();
                                        }}
                                        disabled={
                                          previewImportedManufacturingOrdersImportMutation.isPending ||
                                          confirmImportedManufacturingOrdersImportMutation.isPending
                                        }
                                      >
                                        <MenuItem value="">
                                          <em>Sin unidad</em>
                                        </MenuItem>
                                        <MenuItem value="hours">Horas</MenuItem>
                                        <MenuItem value="minutes">Minutos</MenuItem>
                                      </TextField>
                                    </Stack>
                                  </Stack>
                                </Collapse>
                              </Stack>
                            </Paper>

                            {importedManufacturingOrdersMappableFields.map((field) => {
                              const selectedColumn =
                                importedManufacturingOrdersImportFlow.mapping[field.key] ??
                                importedManufacturingOrdersPreview.auto_mapping[field.key] ??
                                '';

                              const isMissingRequired =
                                field.required &&
                                importedManufacturingOrdersPreview.missing_required_fields.includes(
                                  field.key,
                                );

                              return (
                                <Paper
                                  key={field.key}
                                  elevation={0}
                                  sx={{
                                    borderRadius: 2,
                                    border: isMissingRequired
                                      ? '1px solid rgba(245,158,11,0.45)'
                                      : '1px solid rgba(148,163,184,0.12)',
                                    bgcolor: isMissingRequired
                                      ? 'rgba(245,158,11,0.08)'
                                      : 'rgba(15,23,42,0.34)',
                                    p: 1.25,
                                  }}
                                >
                                  <Stack
                                    direction={{ xs: 'column', md: 'row' }}
                                    spacing={1.5}
                                    alignItems={{ xs: 'stretch', md: 'center' }}
                                    justifyContent="space-between"
                                  >
                                    <Stack spacing={0.35} sx={{ minWidth: 0 }}>
                                      <Stack direction="row" spacing={0.75} alignItems="center">
                                        <Typography sx={{ color: '#f8fafc', fontWeight: 800 }}>
                                          {field.label}
                                        </Typography>

                                        <Chip
                                          size="small"
                                          label={
                                            field.fieldType === 'additional'
                                              ? 'Adicional'
                                              : 'Base'
                                          }
                                          variant="outlined"
                                        />
                                      </Stack>

                                      <Typography
                                        sx={{ color: '#94a3b8', fontSize: '0.82rem' }}
                                      >
                                        {selectedColumn
                                          ? `Detectado: ${selectedColumn}`
                                          : 'Sin columna asignada'}
                                      </Typography>
                                    </Stack>

                                    <TextField
                                      select
                                      size="small"
                                      label="Columna del archivo"
                                      value={selectedColumn}
                                      onChange={(event) => {
                                        const nextColumn = event.target.value;

                                        void (async () => {
                                          const nextFlow =
                                            await applyImportedManufacturingOrdersImportMapping(
                                              importedManufacturingOrdersImportFlow,
                                              field.key,
                                              nextColumn,
                                            );

                                          setImportedManufacturingOrdersImportFlow(nextFlow);
                                        })();
                                      }}
                                      disabled={
                                        previewImportedManufacturingOrdersImportMutation.isPending ||
                                        confirmImportedManufacturingOrdersImportMutation.isPending
                                      }
                                      sx={{ minWidth: { xs: '100%', md: 320 } }}
                                    >
                                      <MenuItem value="">
                                        <em>Sin asignar</em>
                                      </MenuItem>

                                      {importedManufacturingOrdersPreview.columns.map((column) => (
                                        <MenuItem key={`${field.key}-${column}`} value={column}>
                                          {column}
                                        </MenuItem>
                                      ))}
                                    </TextField>
                                  </Stack>
                                </Paper>
                              );
                            })}
                          </Stack>
                        </Paper>
                      )}

                      {importedManufacturingOrdersPreview && (
                        <Paper
                          elevation={0}
                          sx={{
                            borderRadius: 2.5,
                            border: '1px solid rgba(148,163,184,0.12)',
                            bgcolor: 'rgba(2,6,23,0.28)',
                            p: 1.5,
                          }}
                        >
                          <Stack spacing={1.5}>
                            <Stack spacing={0.35}>
                              <Typography sx={{ color: '#f8fafc', fontWeight: 850 }}>
                                Preview final
                              </Typography>

                              <Typography sx={{ color: '#94a3b8', fontSize: '0.86rem' }}>
                                Muestra de las primeras filas usando el mapeo seleccionado.
                              </Typography>
                            </Stack>

                            <Typography sx={{ color: '#94a3b8', fontSize: '0.84rem' }}>
                              Filas detectadas:{' '}
                              {importedManufacturingOrdersPreview.coverage_summary.total_rows}
                              {' · '}
                              Nuevas:{' '}
                              {
                                importedManufacturingOrdersPreview.coverage_summary
                                  .new_manufacturing_orders_count
                              }
                              {' · '}
                              Existentes:{' '}
                              {
                                importedManufacturingOrdersPreview.coverage_summary
                                  .existing_manufacturing_orders_count
                              }
                              {' · '}
                              Duplicadas:{' '}
                              {
                                importedManufacturingOrdersPreview.coverage_summary
                                  .duplicate_manufacturing_orders_in_file_count
                              }
                            </Typography>

                            {importedManufacturingOrdersPreview.missing_required_fields.length >
                              0 && (
                              <Alert severity="warning" variant="outlined">
                                Faltan campos obligatorios en el mapeo:{' '}
                                {importedManufacturingOrdersPreview.missing_required_fields.join(
                                  ', ',
                                )}
                              </Alert>
                            )}

                            {importedManufacturingOrdersPreview.row_warnings
                              .slice(0, 3)
                              .map((warning, index) => (
                                <Alert key={index} severity="info" variant="outlined">
                                  {String(warning.message ?? 'Incidencia detectada en preview.')}
                                </Alert>
                              ))}

                            {importedManufacturingOrdersPreview.row_warnings.length > 3 && (
                              <Typography sx={{ color: '#94a3b8', fontSize: '0.82rem' }}>
                                Hay{' '}
                                {importedManufacturingOrdersPreview.row_warnings.length - 3}{' '}
                                aviso(s) adicional(es).
                              </Typography>
                            )}

                            <TableContainer
                              component={Paper}
                              elevation={0}
                              sx={{
                                borderRadius: 2,
                                border: '1px solid rgba(148,163,184,0.12)',
                                bgcolor: 'rgba(15,23,42,0.32)',
                              }}
                            >
                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    {importedManufacturingOrdersMappableFields
                                      .filter((field) => {
                                        const selectedColumn =
                                          importedManufacturingOrdersImportFlow.mapping[field.key] ??
                                          importedManufacturingOrdersPreview.auto_mapping[field.key] ??
                                          '';

                                        return Boolean(selectedColumn);
                                      })
                                      .map((field) => (
                                        <TableCell key={`preview-header-${field.key}`}>
                                          {field.label}
                                        </TableCell>
                                      ))}
                                  </TableRow>
                                </TableHead>

                                <TableBody>
                                  {importedManufacturingOrdersPreview.preview_rows
                                    .slice(0, 5)
                                    .map((row, index) => (
                                      <TableRow key={`imported-manufacturing-order-preview-${index}`}>
                                        {importedManufacturingOrdersMappableFields
                                          .filter((field) => {
                                            const selectedColumn =
                                              importedManufacturingOrdersImportFlow.mapping[
                                                field.key
                                              ] ??
                                              importedManufacturingOrdersPreview.auto_mapping[
                                                field.key
                                              ] ??
                                              '';

                                            return Boolean(selectedColumn);
                                          })
                                          .map((field) => {
                                            const extraFields = (row.extra_fields ??
                                              {}) as Record<string, unknown>;

                                            const value =
                                              field.fieldType === 'additional'
                                                ? extraFields[field.key]
                                                : row[field.key];

                                            return (
                                              <TableCell
                                                key={`preview-cell-${index}-${field.key}`}
                                              >
                                                {String(value ?? '—')}
                                              </TableCell>
                                            );
                                          })}
                                      </TableRow>
                                    ))}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          </Stack>
                        </Paper>
                      )}
                      {importedManufacturingOrdersConfirmResult && (
                        <Alert severity="success" variant="outlined">
                          Importación completada. Creadas:{' '}
                          {importedManufacturingOrdersConfirmResult.rows_created} · Actualizadas:{' '}
                          {importedManufacturingOrdersConfirmResult.rows_updated} · Importadas:{' '}
                          {importedManufacturingOrdersConfirmResult.rows_imported}
                        </Alert>
                      )}
                    </Stack>
                  </DialogContent>

                  <DialogActions>
                    <Button
                      onClick={() => setImportedManufacturingOrdersImportDialogOpen(false)}
                      disabled={
                        previewImportedManufacturingOrdersImportMutation.isPending ||
                        confirmImportedManufacturingOrdersImportMutation.isPending
                      }
                    >
                      Cerrar
                    </Button>

                    <Button
                      variant="contained"
                      color="success"
                      onClick={() => {
                        if (
                          importedManufacturingOrdersFile &&
                          importedManufacturingOrdersPreview
                        ) {
                          confirmImportedManufacturingOrdersImportMutation.mutate({
                            file: importedManufacturingOrdersFile,
                            mapping: importedManufacturingOrdersPreview.auto_mapping,
                          });
                        }
                      }}
                      disabled={
                        !importedManufacturingOrdersFile ||
                        !importedManufacturingOrdersPreview ||
                        importedManufacturingOrdersPreview.missing_required_fields.length > 0 ||
                        previewImportedManufacturingOrdersImportMutation.isPending ||
                        confirmImportedManufacturingOrdersImportMutation.isPending
                      }
                    >
                      Confirmar importación
                    </Button>
                  </DialogActions>
                </Dialog>
                <Paper
                  elevation={0}
                  sx={{
                    borderRadius: 3,
                    border: '1px solid rgba(148,163,184,0.14)',
                    bgcolor: 'rgba(15,23,42,0.5)',
                    p: 2,
                  }}
                >
                  <Stack spacing={2}>
                    <Typography sx={{ color: '#f8fafc', fontWeight: 900 }}>
                      Datos de la orden
                    </Typography>

                    <Typography
                      sx={{
                        color: '#94a3b8',
                        fontSize: '0.9rem',
                        lineHeight: 1.5,
                      }}
                    >
                      Ajusta la plantilla de la orden y organiza qué información se captura al iniciar la orden y qué datos se reservan para el cierre. La cantidad real debe pedirse al finalizar, no al arrancar.
                    </Typography>

                    <Stack spacing={1.5}>
                      {([
                        {
                          key: 'manufacturing_order_number',
                          title: 'OF',
                          sectionKey: 'identity',
                          sectionTitle: 'Identificación de la orden',
                          sectionDescription:
                            'Datos que identifican la OF, el producto, el cliente y el pedido asociado.',
                          sectionOrder: 10,
                        },
                        {
                          key: 'product',
                          title: 'Producto',
                          sectionKey: 'identity',
                          sectionTitle: 'Identificación de la orden',
                          sectionDescription:
                            'Datos que identifican la OF, el producto, el cliente y el pedido asociado.',
                          sectionOrder: 10,
                        },
                        {
                          key: 'customer',
                          title: 'Cliente',
                          sectionKey: 'identity',
                          sectionTitle: 'Identificación de la orden',
                          sectionDescription:
                            'Datos que identifican la OF, el producto, el cliente y el pedido asociado.',
                          sectionOrder: 10,
                        },
                        {
                          key: 'customer_order',
                          title: 'Pedido cliente',
                          sectionKey: 'identity',
                          sectionTitle: 'Identificación de la orden',
                          sectionDescription:
                            'Datos que identifican la OF, el producto, el cliente y el pedido asociado.',
                          sectionOrder: 10,
                        },
                        {
                          key: 'planned_quantity',
                          title: 'Cantidad orden',
                          sectionKey: 'production',
                          sectionTitle: 'Parámetros de fabricación',
                          sectionDescription:
                            'Datos operativos que se informan al iniciar la orden y alimentan la estimación teórica.',
                          sectionOrder: 20,
                        },
                        {
                          key: 'speed',
                          title: 'Velocidad',
                          sectionKey: 'production',
                          sectionTitle: 'Parámetros de fabricación',
                          sectionDescription:
                            'Datos operativos que se informan al iniciar la orden y alimentan la estimación teórica.',
                          sectionOrder: 20,
                        },
                        {
                          key: 'actual_quantity',
                          title: 'Cantidad real (cierre)',
                          sectionKey: 'closing',
                          sectionTitle: 'Cierre de orden',
                          sectionDescription:
                            'Datos que normalmente deben completarse al finalizar la orden, no al arrancar.',
                          sectionOrder: 30,
                        },
                        {
                          key: 'observations',
                          title: 'Observaciones',
                          sectionKey: 'closing',
                          sectionTitle: 'Cierre de orden',
                          sectionDescription:
                            'Datos que normalmente deben completarse al finalizar la orden, no al arrancar.',
                          sectionOrder: 30,
                        },
                      ] as const)
                        .map((field) => ({
                          ...field,
                          config: operationForm.order_fields_config[field.key],
                        }))
                        .sort(
                          (a, b) =>
                            a.sectionOrder - b.sectionOrder ||
                            a.config.display_order - b.config.display_order,
                        )
                        .map((field, index, fields) => {
                          const config = field.config;
                          const previousField = fields[index - 1] ?? null;
                          const showSectionHeader =
                            !previousField || previousField.sectionKey !== field.sectionKey;
                          
                        return (
                          <Stack key={field.key} spacing={0.75}>
                            {showSectionHeader ? (
                              <Stack
                                spacing={0.25}
                                sx={{
                                  pt: index === 0 ? 0 : 1,
                                  borderTop:
                                    index === 0
                                      ? 'none'
                                      : '1px solid rgba(148,163,184,0.12)',
                                }}
                              >
                                <Typography
                                  sx={{
                                    color: '#f8fafc',
                                    fontWeight: 900,
                                    fontSize: '0.95rem',
                                  }}
                                >
                                  {field.sectionTitle}
                                </Typography>
                                <Typography sx={{ color: '#94a3b8', fontSize: '0.82rem' }}>
                                  {field.sectionDescription}
                                </Typography>
                              </Stack>
                            ) : null}

                            <Paper
                            elevation={0}
                            draggable
                            onDragStart={() => setDraggedOrderFieldKey(field.key)}
                            onDragOver={(event) => {
                              event.preventDefault();
                            }}
                            onDrop={() => {
                              if (draggedOrderFieldKey) {
                                reorderOperationField(draggedOrderFieldKey, field.key);
                              }
                              setDraggedOrderFieldKey(null);
                            }}
                            onDragEnd={() => setDraggedOrderFieldKey(null)}
                            sx={{
                              position: 'relative',
                              overflow: 'hidden',
                              borderRadius: 2.5,
                              border:
                                draggedOrderFieldKey === field.key
                                  ? '1px solid rgba(56,189,248,0.55)'
                                  : '1px solid rgba(148,163,184,0.1)',
                              bgcolor:
                                draggedOrderFieldKey === field.key
                                  ? 'rgba(8,47,73,0.24)'
                                  : 'rgba(2,6,23,0.18)',
                              boxShadow:
                                draggedOrderFieldKey === field.key
                                  ? '0 0 0 1px rgba(56,189,248,0.08), 0 12px 28px rgba(2,6,23,0.34)'
                                  : '0 1px 0 rgba(255,255,255,0.02)',
                              px: 1.25,
                              py: 1,
                              cursor: 'grab',
                              transition:
                                'transform 0.16s ease, border-color 0.16s ease, box-shadow 0.16s ease, background-color 0.16s ease',
                              '&:hover': {
                                borderColor: 'rgba(56,189,248,0.28)',
                                bgcolor: 'rgba(8,47,73,0.18)',
                                boxShadow:
                                  '0 0 0 1px rgba(56,189,248,0.06), 0 12px 26px rgba(2,6,23,0.28)',
                                transform: 'translateY(-1px)',
                              },
                              '&::after': {
                                content: '""',
                                position: 'absolute',
                                inset: 0,
                                borderRadius: 'inherit',
                                pointerEvents: 'none',
                                background:
                                  'linear-gradient(90deg, rgba(56,189,248,0.04) 0%, transparent 18%, transparent 82%, rgba(56,189,248,0.03) 100%)',
                              },
                            }}
                          >
                            <Stack
                              direction={{ xs: 'column', lg: 'row' }}
                              spacing={2}
                              alignItems={{ xs: 'stretch', lg: 'center' }}
                            >
                              <Typography
                                sx={{
                                  color: '#f8fafc',
                                  fontWeight: 850,
                                  width: { xs: '100%', lg: 110 },
                                  flexShrink: 0,
                                  fontSize: '0.95rem',
                                }}
                              >
                                {field.title}
                              </Typography>

                              <TextField
                                size="small"
                                label="Etiqueta"
                                value={config.label}
                                onChange={(event) =>
                                  setOperationForm((current) =>
                                    current
                                      ? {
                                          ...current,
                                          order_fields_config: {
                                            ...current.order_fields_config,
                                            [field.key]: {
                                              ...current.order_fields_config[field.key],
                                              label: event.target.value,
                                            },
                                          },
                                        }
                                      : current,
                                  )
                                }
                                sx={{
                                  width: { xs: '100%', lg: 290 },
                                  flexShrink: 0,
                                  '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                    transition:
                                      'border-color 0.16s ease, box-shadow 0.16s ease, background-color 0.16s ease',
                                    '& fieldset': {
                                      borderColor: 'rgba(148,163,184,0.22)',
                                    },
                                    '&:hover fieldset': {
                                      borderColor: 'rgba(56,189,248,0.34)',
                                    },
                                    '&.Mui-focused fieldset': {
                                      borderColor: 'rgba(56,189,248,0.62)',
                                      boxShadow: '0 0 0 2px rgba(56,189,248,0.08)',
                                    },
                                  },
                                }}
                              />

                              <TextField
                                select
                                size="small"
                                label="Origen"
                                value={config.source}
                                onChange={(event) =>
                                  setOperationForm((current) =>
                                    current
                                      ? {
                                          ...current,
                                          order_fields_config: {
                                            ...current.order_fields_config,
                                            [field.key]: {
                                              ...current.order_fields_config[field.key],
                                              source: event.target.value as 'manual' | 'automatic',
                                            },
                                          },
                                        }
                                      : current,
                                  )
                                }
                                sx={{
                                  width: { xs: '100%', lg: 190 },
                                  flexShrink: 0,
                                  '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                    transition:
                                      'border-color 0.16s ease, box-shadow 0.16s ease, background-color 0.16s ease',
                                    '& fieldset': {
                                      borderColor: 'rgba(148,163,184,0.22)',
                                    },
                                    '&:hover fieldset': {
                                      borderColor: 'rgba(56,189,248,0.34)',
                                    },
                                    '&.Mui-focused fieldset': {
                                      borderColor: 'rgba(56,189,248,0.62)',
                                      boxShadow: '0 0 0 2px rgba(56,189,248,0.08)',
                                    },
                                  },
                                }}
                              >
                                <MenuItem value="manual">Manual</MenuItem>
                                <MenuItem value="automatic">Automático</MenuItem>
                              </TextField>

                              <Stack
                                direction="row"
                                spacing={2.25}
                                flexWrap="wrap"
                                useFlexGap
                                alignItems="center"
                                sx={{
                                  flex: 1,
                                  minWidth: { xs: '100%', lg: 340 },
                                }}
                              >
                                <Stack direction="row" spacing={0.8} alignItems="center">
                                  <Switch
                                    checked={config.visible}
                                    onChange={(event) =>
                                      setOperationForm((current) =>
                                        current
                                          ? {
                                              ...current,
                                              order_fields_config: {
                                                ...current.order_fields_config,
                                                [field.key]: {
                                                  ...current.order_fields_config[field.key],
                                                  visible: event.target.checked,
                                                },
                                              },
                                            }
                                          : current,
                                      )
                                    }
                                  />
                                  <Typography sx={{ color: '#e2e8f0', fontSize: '0.92rem' }}>
                                    Visible
                                  </Typography>
                                </Stack>

                                <Stack direction="row" spacing={0.8} alignItems="center">
                                  <Switch
                                    checked={config.required}
                                    onChange={(event) =>
                                      setOperationForm((current) =>
                                        current
                                          ? {
                                              ...current,
                                              order_fields_config: {
                                                ...current.order_fields_config,
                                                [field.key]: {
                                                  ...current.order_fields_config[field.key],
                                                  required: event.target.checked,
                                                },
                                              },
                                            }
                                          : current,
                                      )
                                    }
                                  />
                                  <Typography sx={{ color: '#e2e8f0', fontSize: '0.92rem' }}>
                                    Obligatorio al cierre
                                  </Typography>
                                </Stack>
                              </Stack>

                              <Stack
                                justifyContent="center"
                                alignItems="center"
                                sx={{
                                  width: { xs: '100%', lg: 42 },
                                  minHeight: 38,
                                  ml: { lg: 'auto' },
                                  flexShrink: 0,
                                  borderRadius: 2,
                                  border: '1px dashed rgba(148,163,184,0.16)',
                                  bgcolor: 'rgba(15,23,42,0.22)',
                                  transition:
                                    'border-color 0.16s ease, background-color 0.16s ease, transform 0.16s ease',
                                  '&:hover': {
                                    borderColor: 'rgba(56,189,248,0.34)',
                                    bgcolor: 'rgba(8,47,73,0.22)',
                                    transform: 'scale(1.03)',
                                  },
                                }}
                              >
                                <DragIndicatorRoundedIcon
                                  sx={{ color: '#94a3b8', fontSize: '1.05rem' }}
                                />
                              </Stack>
                            </Stack>
                          </Paper>
                        </Stack>
                        );
                      })}
                    </Stack>

                    <Paper
                      elevation={0}
                      sx={{
                        borderRadius: 3,
                        border: '1px solid rgba(56,189,248,0.18)',
                        bgcolor: 'rgba(8,47,73,0.14)',
                        p: 1.5,
                        mt: 0.5,
                      }}
                    >
                      <Stack
                        direction={{ xs: 'column', md: 'row' }}
                        spacing={1.5}
                        alignItems={{ xs: 'stretch', md: 'center' }}
                        justifyContent="space-between"
                      >
                        <Stack spacing={0.4}>
                          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                            <Typography sx={{ color: '#f8fafc', fontWeight: 900 }}>
                              Campos propios de la operación
                            </Typography>
                            <Chip
                              size="small"
                              label={`${operationForm.additional_fields_config.length} campo${
                                operationForm.additional_fields_config.length === 1 ? '' : 's'
                              }`}
                              variant="outlined"
                              sx={{
                                color: '#bae6fd',
                                borderColor: 'rgba(56,189,248,0.24)',
                                bgcolor: 'rgba(8,47,73,0.18)',
                                fontWeight: 800,
                              }}
                            />
                          </Stack>

                          <Typography sx={{ color: '#94a3b8', fontSize: '0.86rem' }}>
                            Añade datos específicos de esta operación, como referencias internas,
                            utillaje, moldes, salidas, controles auxiliares o multiplicadores
                            operativos. Estos campos complementan la orden sin alterar el núcleo
                            del sistema.
                          </Typography>
                        </Stack>

                        <Button
                          variant="outlined"
                          startIcon={<AddRoundedIcon />}
                          onClick={addAdditionalOperationField}
                          sx={{
                            borderRadius: 2,
                            alignSelf: { xs: 'stretch', md: 'center' },
                            flexShrink: 0,
                          }}
                        >
                          Añadir campo
                        </Button>
                      </Stack>
                    </Paper>

                    {operationForm.additional_fields_config.length > 0 ? (
                      <Stack spacing={1.5}>
                        {[...operationForm.additional_fields_config]
                          .sort((a, b) => a.display_order - b.display_order)
                          .map((field) => (
                            <Paper
                              key={field.key}
                              elevation={0}
                              draggable
                              onDragStart={() => setDraggedAdditionalFieldKey(field.key)}
                              onDragOver={(event) => {
                                event.preventDefault();
                              }}
                              onDrop={() => {
                                if (draggedAdditionalFieldKey) {
                                  reorderAdditionalOperationField(
                                    draggedAdditionalFieldKey,
                                    field.key,
                                  );
                                }
                                setDraggedAdditionalFieldKey(null);
                              }}
                              onDragEnd={() => setDraggedAdditionalFieldKey(null)}
                              sx={{
                                borderRadius: 2.5,
                                border:
                                  draggedAdditionalFieldKey === field.key
                                    ? '1px solid rgba(56,189,248,0.55)'
                                    : '1px solid rgba(148,163,184,0.1)',
                                bgcolor:
                                  draggedAdditionalFieldKey === field.key
                                    ? 'rgba(8,47,73,0.24)'
                                    : 'rgba(2,6,23,0.18)',
                                px: 1.25,
                                py: 1,
                                cursor: 'grab',
                              }}
                            >
                              <Stack
                                direction={{ xs: 'column', lg: 'row' }}
                                spacing={2}
                                alignItems={{ xs: 'stretch', lg: 'center' }}
                              >
                                <Stack
                                  spacing={0.15}
                                  sx={{
                                    width: { xs: '100%', lg: 130 },
                                    flexShrink: 0,
                                  }}
                                >
                                  <Typography
                                    sx={{
                                      color: '#f8fafc',
                                      fontWeight: 900,
                                      fontSize: '0.92rem',
                                    }}
                                  >
                                    Campo propio
                                  </Typography>
                                  <Typography sx={{ color: '#64748b', fontSize: '0.72rem' }}>
                                    {field.key}
                                  </Typography>
                                </Stack>

                                <TextField
                                  size="small"
                                  label="Etiqueta"
                                  value={field.label}
                                  onChange={(event) =>
                                    updateAdditionalOperationField(field.key, {
                                      label: event.target.value,
                                    })
                                  }
                                  sx={{ width: { xs: '100%', lg: 290 }, flexShrink: 0 }}
                                />

                                <TextField
                                  select
                                  size="small"
                                  label="Tipo"
                                  value={field.field_type}
                                  onChange={(event) =>
                                    updateAdditionalOperationField(field.key, {
                                      field_type: event.target.value as 'text' | 'number',
                                    })
                                  }
                                  sx={{ width: { xs: '100%', lg: 160 }, flexShrink: 0 }}
                                >
                                  <MenuItem value="text">Texto</MenuItem>
                                  <MenuItem value="number">Número</MenuItem>
                                </TextField>

                                <TextField
                                  select
                                  size="small"
                                  label="Ubicación"
                                  value={
                                    field.section_key ??
                                    (field.field_type === 'number' ? 'production' : 'identity')
                                  }
                                  onChange={(event) =>
                                    updateAdditionalOperationField(field.key, {
                                      section_key: event.target.value as
                                        | 'identity'
                                        | 'production'
                                        | 'additional'
                                        | 'closing',
                                    })
                                  }
                                  sx={{ width: { xs: '100%', lg: 230 }, flexShrink: 0 }}
                                >
                                  <MenuItem value="identity">Identificación</MenuItem>
                                  <MenuItem value="production">Parámetros fabricación</MenuItem>
                                  <MenuItem value="additional">Datos adicionales</MenuItem>
                                  <MenuItem value="closing" disabled>
                                    Cierre de orden · Próximamente
                                  </MenuItem>
                                </TextField>

                                <Stack
                                  direction="row"
                                  spacing={2.25}
                                  flexWrap="wrap"
                                  useFlexGap
                                  alignItems="center"
                                  sx={{
                                    flex: 1,
                                    minWidth: { xs: '100%', lg: 320 },
                                    px: { xs: 0, lg: 1 },
                                  }}
                                >
                                  <Stack direction="row" spacing={0.8} alignItems="center">
                                    <Switch
                                      checked={field.visible}
                                      onChange={(event) =>
                                        updateAdditionalOperationField(field.key, {
                                          visible: event.target.checked,
                                        })
                                      }
                                    />
                                    <Typography sx={{ color: '#e2e8f0', fontSize: '0.92rem' }}>
                                      Visible
                                    </Typography>
                                  </Stack>

                                  <Stack direction="row" spacing={0.8} alignItems="center">
                                    <Switch
                                      checked={field.required}
                                      onChange={(event) =>
                                        updateAdditionalOperationField(field.key, {
                                          required: event.target.checked,
                                        })
                                      }
                                    />
                                    <Typography sx={{ color: '#e2e8f0', fontSize: '0.92rem' }}>
                                      Obligatorio al cierre
                                    </Typography>
                                  </Stack>
                                </Stack>

                                <Button
                                  variant="outlined"
                                  color="error"
                                  startIcon={<DeleteOutlineRoundedIcon />}
                                  onClick={() => removeAdditionalOperationField(field.key)}
                                  sx={{
                                    flexShrink: 0,
                                    borderRadius: 2,
                                    minWidth: { xs: '100%', lg: 110 },
                                  }}
                                >
                                  Quitar
                                </Button>

                                <Stack
                                  justifyContent="center"
                                  alignItems="center"
                                  sx={{
                                    width: { xs: '100%', lg: 42 },
                                    minHeight: 38,
                                    flexShrink: 0,
                                    borderRadius: 2,
                                    border: '1px dashed rgba(148,163,184,0.16)',
                                    bgcolor: 'rgba(15,23,42,0.22)',
                                  }}
                                >
                                  <DragIndicatorRoundedIcon
                                    sx={{ color: '#94a3b8', fontSize: '1.05rem' }}
                                  />
                                </Stack>
                              </Stack>
                            </Paper>
                          ))}
                      </Stack>
                    ) : null}
                  </Stack>
                </Paper>

                <Paper
                  elevation={0}
                  sx={{
                    borderRadius: 3,
                    border: '1px solid rgba(56,189,248,0.18)',
                    bgcolor: 'rgba(8,47,73,0.12)',
                    p: 2,
                  }}
                >
                  <Stack spacing={1.5}>
                    <Stack
                      direction={{ xs: 'column', md: 'row' }}
                      justifyContent="space-between"
                      alignItems={{ xs: 'stretch', md: 'center' }}
                      spacing={1.5}
                    >
                      <Stack spacing={0.35}>
                        <Typography sx={{ color: '#f8fafc', fontWeight: 900 }}>
                          Diseño del parte de trabajo
                        </Typography>
                        <Typography sx={{ color: '#94a3b8', fontSize: '0.86rem' }}>
                          Vista segura del layout visual del parte. En esta fase no modifica todavía
                          el puesto operario.
                        </Typography>
                      </Stack>

                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        flexWrap="wrap"
                        useFlexGap
                        sx={{ alignSelf: { xs: 'flex-start', md: 'center' } }}
                      >
                        <Chip
                          size="small"
                          label="Editor visual seguro"
                          variant="outlined"
                          sx={{
                            color: '#bae6fd',
                            borderColor: 'rgba(56,189,248,0.24)',
                            bgcolor: 'rgba(8,47,73,0.18)',
                            fontWeight: 850,
                          }}
                        />

                        <Button
                          size="small"
                          variant="outlined"
                          onClick={resetProductionReportLayoutConfig}
                        >
                          Restaurar diseño
                        </Button>
                      </Stack>
                    </Stack>

                    {(() => {
                      const orderFieldEntries = operationForm.order_fields_config;

                      const getOrderFieldPreview = (
                        key:
                          | 'manufacturing_order_number'
                          | 'product'
                          | 'customer'
                          | 'customer_order'
                          | 'planned_quantity'
                          | 'actual_quantity'
                          | 'speed'
                          | 'observations',
                        fallbackLabel: string,
                      ) => {
                        const config = orderFieldEntries[key];

                        if (!config?.visible) {
                          return null;
                        }

                        return {
                          key,
                          label: config.label?.trim() || fallbackLabel,
                          required: Boolean(config.required),
                          type: 'base' as const,
                        };
                      };

                      const additionalFieldByKey = new Map(
                        operationForm.additional_fields_config.map((field) => [
                          field.key,
                          field,
                        ]),
                      );

                      const layoutConfig =
                        normalizeProductionReportFormLayoutConfig(operationForm);

                      const resolveLayoutFieldPreview = (
                        fieldRef: ProductionReportFormLayoutConfig['sections'][number]['field_refs'][number],
                      ) => {
                        if (fieldRef.source === 'order_field') {
                          const orderFieldKey = fieldRef.key as ProductionReportOrderFieldKey;
                          const preview = getOrderFieldPreview(
                            orderFieldKey,
                            productionReportOrderFieldFallbackLabels[orderFieldKey] ??
                              fieldRef.key,
                          );

                          return preview
                            ? {
                                ...preview,
                                layoutFieldRef: fieldRef,
                              }
                            : null;
                        }

                        const additionalField = additionalFieldByKey.get(fieldRef.key);

                        if (
                          !additionalField ||
                          !additionalField.is_active ||
                          !additionalField.visible
                        ) {
                          return null;
                        }

                        return {
                          key: additionalField.key,
                          label: additionalField.label?.trim() || additionalField.key,
                          required: Boolean(additionalField.required),
                          type: 'additional' as const,
                          fieldType: additionalField.field_type,
                          layoutFieldRef: fieldRef,
                        };
                      };

                      const previewSections = layoutConfig.sections.map((section) => {
                        const sectionDefinition =
                          productionReportFormLayoutSectionDefinitions.find(
                            (item) => item.key === section.key,
                          );

                        return {
                          key: section.key,
                          title: sectionDefinition?.label ?? section.label,
                          description: sectionDefinition?.description ?? '',
                          tone: sectionDefinition?.tone ?? '#38bdf8',
                          fields: section.field_refs
                            .map(resolveLayoutFieldPreview)
                            .filter(Boolean),
                        };
                      });

                      return (
                        <Box
                          sx={{
                            display: 'grid',
                            gridTemplateColumns: {
                              xs: '1fr',
                              md: 'repeat(2, minmax(0, 1fr))',
                            },
                            gap: 1.25,
                          }}
                        >
                          {previewSections.map((section) => (
                            <Paper
                              key={section.key}
                              elevation={0}
                              onDragOver={(event) => {
                                if (draggedProductionReportLayoutFieldRef) {
                                  event.preventDefault();
                                }
                              }}
                              onDrop={() => {
                                if (draggedProductionReportLayoutFieldRef) {
                                  moveProductionReportLayoutField(
                                    draggedProductionReportLayoutFieldRef,
                                    section.key as ProductionReportFormLayoutSectionKey,
                                  );
                                }

                                setDraggedProductionReportLayoutFieldRef(null);
                              }}
                              sx={{
                                borderRadius: 2.75,
                                border: '1px solid rgba(148,163,184,0.14)',
                                bgcolor: 'rgba(15,23,42,0.34)',
                                p: 1.35,
                                position: 'relative',
                                overflow: 'hidden',
                                '&::before': {
                                  content: '""',
                                  position: 'absolute',
                                  left: 0,
                                  top: 0,
                                  bottom: 0,
                                  width: 3,
                                  bgcolor: section.tone,
                                },
                              }}
                            >
                              <Stack spacing={1.1} sx={{ position: 'relative' }}>
                                <Stack spacing={0.25}>
                                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                                    <Typography sx={{ color: '#f8fafc', fontWeight: 900 }}>
                                      {section.title}
                                    </Typography>
                                    <Chip
                                      size="small"
                                      label={`${section.fields.length} campo${
                                        section.fields.length === 1 ? '' : 's'
                                      }`}
                                      variant="outlined"
                                      sx={{
                                        height: 22,
                                        color: '#cbd5e1',
                                        borderColor: 'rgba(148,163,184,0.20)',
                                        bgcolor: 'rgba(15,23,42,0.28)',
                                        fontWeight: 800,
                                        fontSize: '0.68rem',
                                      }}
                                    />
                                  </Stack>
                                  <Typography sx={{ color: '#94a3b8', fontSize: '0.78rem' }}>
                                    {section.description}
                                  </Typography>
                                </Stack>

                                {section.fields.length === 0 ? (
                                  <Typography
                                    sx={{
                                      color: '#64748b',
                                      fontSize: '0.8rem',
                                      fontStyle: 'italic',
                                    }}
                                  >
                                    Sin campos visibles en esta sección.
                                  </Typography>
                                ) : (
                                  <Box
                                    sx={{
                                      display: 'grid',
                                      gridTemplateColumns: {
                                        xs: '1fr',
                                        lg: 'repeat(2, minmax(0, 1fr))',
                                      },
                                      gap: 0.75,
                                    }}
                                  >
                                    {section.fields.map((field) => {
                                      if (!field) return null;

                                      const isDraggingLayoutField =
                                        draggedProductionReportLayoutFieldRef &&
                                        getProductionReportLayoutFieldRefKey(
                                          draggedProductionReportLayoutFieldRef,
                                        ) ===
                                          getProductionReportLayoutFieldRefKey(
                                            field.layoutFieldRef,
                                          );

                                      return (
                                        <Paper
                                          key={`${section.key}-${field.key}`}
                                          elevation={0}
                                          draggable
                                          onDragStart={() =>
                                            setDraggedProductionReportLayoutFieldRef(
                                              field.layoutFieldRef,
                                            )
                                          }
                                          onDragOver={(event) => {
                                            if (draggedProductionReportLayoutFieldRef) {
                                              event.preventDefault();
                                            }
                                          }}
                                          onDrop={(event) => {
                                            event.stopPropagation();

                                            if (draggedProductionReportLayoutFieldRef) {
                                              moveProductionReportLayoutField(
                                                draggedProductionReportLayoutFieldRef,
                                                section.key as ProductionReportFormLayoutSectionKey,
                                                field.layoutFieldRef,
                                              );
                                            }

                                            setDraggedProductionReportLayoutFieldRef(null);
                                          }}
                                          onDragEnd={() =>
                                            setDraggedProductionReportLayoutFieldRef(null)
                                          }
                                          sx={{
                                            borderRadius: 2,
                                            border: isDraggingLayoutField
                                              ? '1px solid rgba(56,189,248,0.55)'
                                              : '1px solid rgba(148,163,184,0.12)',
                                            bgcolor: isDraggingLayoutField
                                              ? 'rgba(8,47,73,0.24)'
                                              : 'rgba(2,6,23,0.26)',
                                            px: 1,
                                            py: 0.85,
                                            cursor: 'grab',
                                          }}
                                        >
                                          <Stack spacing={0.25}>
                                            <Stack
                                              direction="row"
                                              justifyContent="space-between"
                                              alignItems="center"
                                              spacing={1}
                                            >
                                              <Typography
                                                sx={{
                                                  color: '#f8fafc',
                                                  fontWeight: 850,
                                                  fontSize: '0.84rem',
                                                  overflow: 'hidden',
                                                  textOverflow: 'ellipsis',
                                                  whiteSpace: 'nowrap',
                                                }}
                                              >
                                                {field.label}
                                              </Typography>

                                              <Stack direction="row" spacing={0.5} alignItems="center">
                                                {field.required ? (
                                                  <Chip
                                                    size="small"
                                                    label="Oblig."
                                                    variant="outlined"
                                                    sx={{
                                                      height: 19,
                                                      color: '#fde68a',
                                                      borderColor: 'rgba(251,191,36,0.24)',
                                                      bgcolor: 'rgba(120,53,15,0.12)',
                                                      fontWeight: 800,
                                                      fontSize: '0.64rem',
                                                      flexShrink: 0,
                                                    }}
                                                  />
                                                ) : null}

                                                <DragIndicatorRoundedIcon
                                                  sx={{
                                                    color: '#94a3b8',
                                                    fontSize: '1rem',
                                                    flexShrink: 0,
                                                  }}
                                                />
                                              </Stack>
                                            </Stack>

                                            <Typography sx={{ color: '#64748b', fontSize: '0.68rem' }}>
                                              {field.type === 'additional'
                                                ? `Campo propio · ${field.fieldType === 'number' ? 'Número' : 'Texto'}`
                                                : 'Campo base del sistema'}
                                            </Typography>
                                          </Stack>
                                        </Paper>
                                      );
                                    })}
                                  </Box>
                                )}
                              </Stack>
                            </Paper>
                          ))}
                        </Box>
                      );
                    })()}
                  </Stack>
                </Paper>

                <Paper
                  elevation={0}
                  sx={{
                    borderRadius: 3,
                    border: '1px solid rgba(148,163,184,0.14)',
                    bgcolor: 'rgba(15,23,42,0.5)',
                    p: 2,
                  }}
                >
                  <Stack spacing={2}>
                    <Typography sx={{ color: '#f8fafc', fontWeight: 900 }}>
                      Parámetros de producción
                    </Typography>

                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
                      <TextField
                        select
                        size="small"
                        label="Unidad principal"
                        value={operationForm.production_params.production_unit}
                        onChange={(event) =>
                          setOperationForm((current) =>
                            current
                              ? {
                                  ...current,
                                  production_params: {
                                    ...current.production_params,
                                    production_unit: event.target.value as 'meters' | 'kg' | 'units',
                                  },
                                }
                              : current,
                          )
                        }
                        fullWidth
                      >
                        <MenuItem value="meters">Metros</MenuItem>
                        <MenuItem value="kg">Kg</MenuItem>
                        <MenuItem value="units">Unidades</MenuItem>
                      </TextField>

                      <TextField
                        size="small"
                        type="number"
                        label="Velocidad por defecto"
                        helperText="Valor base para precargar o estimar, si la operación lo permite."
                        value={operationForm.production_params.default_speed ?? ''}
                        onChange={(event) =>
                          setOperationForm((current) =>
                            current
                              ? {
                                  ...current,
                                  production_params: {
                                    ...current.production_params,
                                    default_speed:
                                      event.target.value === ''
                                        ? null
                                        : Number(event.target.value),
                                  },
                                }
                              : current,
                          )
                        }
                        fullWidth
                      />

                      <TextField
                        size="small"
                        type="number"
                        label="Tiempo preparación estándar (min)"
                        helperText="Tiempo teórico que puede sumarse a la estimación, no al tiempo real."
                        value={operationForm.production_params.setup_time_minutes}
                        onChange={(event) =>
                          setOperationForm((current) =>
                            current
                              ? {
                                  ...current,
                                  production_params: {
                                    ...current.production_params,
                                    setup_time_minutes: Number(event.target.value),
                                  },
                                }
                              : current,
                          )
                        }
                        fullWidth
                      />
                    </Stack>

                    <TextField
                      size="small"
                      type="number"
                      label="Tolerancia frente a duración teórica (%)"
                      helperText="Margen permitido antes de considerar que una orden se desvía de su estimación."
                      value={operationForm.production_params.deviation_tolerance_pct}
                      onChange={(event) =>
                        setOperationForm((current) =>
                          current
                            ? {
                                ...current,
                                production_params: {
                                  ...current.production_params,
                                  deviation_tolerance_pct: Number(event.target.value),
                                },
                              }
                            : current,
                        )
                      }
                      fullWidth
                    />

                    <Box
                      sx={{
                        p: 1.5,
                        borderRadius: 2,
                        border: '1px solid rgba(56,189,248,0.18)',
                        bgcolor: 'rgba(14,165,233,0.05)',
                      }}
                    >
                      <Stack spacing={1}>
                        <Box>
                          <Typography sx={{ color: '#e2e8f0', fontWeight: 850 }}>
                            Calendario semanal de producción
                          </Typography>
                          <Typography sx={{ color: '#94a3b8', fontSize: '0.78rem' }}>
                            Los turnos activos determinan las horas disponibles por día.
                            Selecciona qué días aportan capacidad productiva.
                          </Typography>
                        </Box>

                        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                          {PRODUCTION_WORKING_WEEKDAYS.map((weekday) => {
                            const selectedDays =
                              operationForm.production_params.working_weekdays ??
                              [0, 1, 2, 3, 4];
                            const selected = selectedDays.includes(weekday.value);
                            const isLastSelected = selected && selectedDays.length === 1;

                            return (
                              <Chip
                                key={weekday.value}
                                label={weekday.label}
                                clickable
                                disabled={isLastSelected}
                                color={selected ? 'primary' : 'default'}
                                variant={selected ? 'filled' : 'outlined'}
                                onClick={() =>
                                  setOperationForm((current) => {
                                    if (!current) return current;

                                    const currentDays =
                                      current.production_params.working_weekdays ??
                                      [0, 1, 2, 3, 4];
                                    const nextDays = selected
                                      ? currentDays.filter((value) => value !== weekday.value)
                                      : [...currentDays, weekday.value];

                                    return {
                                      ...current,
                                      production_params: {
                                        ...current.production_params,
                                        working_weekdays: [...nextDays].sort((a, b) => a - b),
                                      },
                                    };
                                  })
                                }
                                sx={{ fontWeight: 800 }}
                              />
                            );
                          })}
                        </Stack>
                      </Stack>
                    </Box>

                    <TextField
                      size="small"
                      type="number"
                      label="Antelación de fabricación (días productivos)"
                      helperText="Con valor 1, una expedición debe quedar fabricada el día productivo anterior."
                      value={operationForm.production_params.manufacturing_deadline_buffer_working_days ?? 1}
                      inputProps={{ min: 0, max: 30, step: 1 }}
                      onChange={(event) =>
                        setOperationForm((current) =>
                          current
                            ? {
                                ...current,
                                production_params: {
                                  ...current.production_params,
                                  manufacturing_deadline_buffer_working_days: Math.min(
                                    Math.max(Math.trunc(Number(event.target.value) || 0), 0),
                                    30,
                                  ),
                                },
                              }
                            : current,
                        )
                      }
                      fullWidth
                    />

                    <TextField
                      size="small"
                      type="number"
                      label="Plazo de entrega de los pedidos (días laborables)"
                      helperText="Marca del plazo en las tarjetas de carga del Panel de planta. Con 20 días la escala queda 0–30."
                      value={operationForm.production_params.workload_target_working_days ?? 20}
                      inputProps={{ min: 1, max: 60, step: 1 }}
                      onChange={(event) =>
                        setOperationForm((current) =>
                          current
                            ? {
                                ...current,
                                production_params: {
                                  ...current.production_params,
                                  workload_target_working_days: Math.min(
                                    Math.max(
                                      Math.trunc(Number(event.target.value) || 20),
                                      1,
                                    ),
                                    60,
                                  ),
                                },
                              }
                            : current,
                        )
                      }
                      fullWidth
                    />

                    <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Switch
                          checked={operationForm.production_params.manufacturing_order_required}
                          onChange={(event) =>
                            setOperationForm((current) =>
                              current
                                ? {
                                    ...current,
                                    production_params: {
                                      ...current.production_params,
                                      manufacturing_order_required: event.target.checked,
                                    },
                                  }
                                : current,
                            )
                          }
                        />
                        <Typography sx={{ color: '#e2e8f0' }}>OF obligatoria</Typography>
                      </Stack>

                      <Stack direction="row" spacing={1} alignItems="center">
                        <Switch
                          checked={operationForm.production_params.allow_manual_order}
                          onChange={(event) =>
                            setOperationForm((current) =>
                              current
                                ? {
                                    ...current,
                                    production_params: {
                                      ...current.production_params,
                                      allow_manual_order: event.target.checked,
                                    },
                                  }
                                : current,
                            )
                          }
                        />
                        <Typography sx={{ color: '#e2e8f0' }}>Permitir orden manual</Typography>
                      </Stack>
                    </Stack>
                  </Stack>
                </Paper>

                <Paper
                  elevation={0}
                  sx={{
                    borderRadius: 3,
                    border: '1px solid rgba(148,163,184,0.14)',
                    bgcolor: 'rgba(15,23,42,0.5)',
                    p: 2,
                  }}
                >
                  <Stack spacing={2}>
                    <Stack spacing={0.5}>
                      <Typography sx={{ color: '#f8fafc', fontWeight: 900 }}>
                        Estimaciones
                      </Typography>
                      <Typography sx={{ color: '#94a3b8', fontSize: '0.9rem' }}>
                        Define cómo interpretar la velocidad para calcular una duración teórica
                        basada en la cantidad de la orden.
                      </Typography>
                    </Stack>

                    <Alert severity="info" variant="outlined">
                      La estimación no modifica el tiempo real ni bloquea el trabajo del operario.
                      Calcula una duración teórica a partir de la cantidad de la orden, la velocidad y,
                      si aplica, un multiplicador numérico como salidas. La cantidad real se reserva
                      para el cierre y el análisis final.
                    </Alert>

                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                      <TextField
                        select
                        size="small"
                        label="Modo de estimación"
                        value={operationForm.estimation_config.mode}
                        onChange={(event) =>
                          setOperationForm((current) => {
                            if (!current) return current;

                            const nextMode =
                              event.target
                                .value as UpdateProductionOperationSettingsPayload['estimation_config']['mode'];

                            return {
                              ...current,
                              estimation_config: {
                                ...current.estimation_config,
                                mode: nextMode,
                                enable_estimated_duration: nextMode !== 'none',
                                multiplier_field_key:
                                  nextMode === 'quantity_by_speed_and_multiplier'
                                    ? current.estimation_config.multiplier_field_key
                                    : null,
                              },
                            };
                          })
                        }
                        fullWidth
                      >
                        <MenuItem value="none">Sin estimación</MenuItem>
                        <MenuItem value="quantity_by_speed">
                          Cantidad prevista / velocidad
                        </MenuItem>
                        <MenuItem value="quantity_by_speed_and_multiplier">
                          Cantidad prevista / (velocidad × multiplicador)
                        </MenuItem>
                      </TextField>

                      <TextField
                        select
                        size="small"
                        label="Velocidad expresada"
                        value={operationForm.estimation_config.speed_time_unit}
                        onChange={(event) =>
                          setOperationForm((current) =>
                            current
                              ? {
                                  ...current,
                                  estimation_config: {
                                    ...current.estimation_config,
                                    speed_time_unit:
                                      event.target
                                        .value as UpdateProductionOperationSettingsPayload['estimation_config']['speed_time_unit'],
                                  },
                                }
                              : current,
                          )
                        }
                        disabled={operationForm.estimation_config.mode === 'none'}
                        fullWidth
                      >
                        <MenuItem value="minute">Por minuto</MenuItem>
                        <MenuItem value="hour">Por hora</MenuItem>
                      </TextField>

                      <TextField
                        select
                        size="small"
                        label="Campo multiplicador"
                        value={operationForm.estimation_config.multiplier_field_key ?? ''}
                        onChange={(event) =>
                          setOperationForm((current) =>
                            current
                              ? {
                                  ...current,
                                  estimation_config: {
                                    ...current.estimation_config,
                                    multiplier_field_key: event.target.value || null,
                                  },
                                }
                              : current,
                          )
                        }
                        disabled={
                          operationForm.estimation_config.mode !==
                          'quantity_by_speed_and_multiplier'
                        }
                        helperText={
                          numericAdditionalFields.length === 0
                            ? 'Crea antes un campo adicional numérico, por ejemplo salidas.'
                            : 'Debe ser un campo adicional numérico visible.'
                        }
                        fullWidth
                      >
                        <MenuItem value="">Sin campo seleccionado</MenuItem>
                        {numericAdditionalFields.map((field) => (
                          <MenuItem key={field.key} value={field.key}>
                            {field.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Stack>

                    <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Switch
                          checked={operationForm.estimation_config.enable_estimated_end_time}
                          disabled={operationForm.estimation_config.mode === 'none'}
                          onChange={(event) =>
                            setOperationForm((current) =>
                              current
                                ? {
                                    ...current,
                                    estimation_config: {
                                      ...current.estimation_config,
                                      enable_estimated_end_time: event.target.checked,
                                    },
                                  }
                                : current,
                            )
                          }
                        />
                        <Typography sx={{ color: '#e2e8f0' }}>Hora teórica de fin</Typography>
                      </Stack>

                      <Stack direction="row" spacing={1} alignItems="center">
                        <Switch
                          checked={operationForm.estimation_config.include_setup_time}
                          disabled={operationForm.estimation_config.mode === 'none'}
                          onChange={(event) =>
                            setOperationForm((current) =>
                              current
                                ? {
                                    ...current,
                                    estimation_config: {
                                      ...current.estimation_config,
                                      include_setup_time: event.target.checked,
                                    },
                                  }
                                : current,
                            )
                          }
                        />
                        <Typography sx={{ color: '#e2e8f0' }}>
                          Sumar preparación estándar
                        </Typography>
                      </Stack>
                    </Stack>

                    {operationForm.estimation_config.mode !== 'none' ? (
                      <Alert severity="success" variant="outlined">
                        Fórmula activa:{' '}
                        {operationForm.estimation_config.mode === 'quantity_by_speed'
                          ? 'cantidad prevista / velocidad'
                          : `cantidad prevista / (velocidad × ${
                              numericAdditionalFields.find(
                                (field) =>
                                  field.key ===
                                  operationForm.estimation_config.multiplier_field_key,
                              )?.label ?? 'multiplicador'
                            })`}
                        {operationForm.estimation_config.include_setup_time
                          ? ' + preparación'
                          : ''}
                        .
                      </Alert>
                    ) : null}
                  </Stack>
                </Paper>

                <Paper
                  elevation={0}
                  sx={{
                    borderRadius: 3,
                    border: '1px solid rgba(148,163,184,0.14)',
                    bgcolor: 'rgba(15,23,42,0.5)',
                    p: 2,
                  }}
                >
                  <Stack spacing={2}>
                    <Typography sx={{ color: '#f8fafc', fontWeight: 900 }}>
                      Reglas
                    </Typography>

                    <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Switch
                          checked={operationForm.validation_rules.block_close_if_open_stop_exists}
                          onChange={(event) =>
                            setOperationForm((current) =>
                              current
                                ? {
                                    ...current,
                                    validation_rules: {
                                      ...current.validation_rules,
                                      block_close_if_open_stop_exists: event.target.checked,
                                    },
                                  }
                                : current,
                            )
                          }
                        />
                        <Typography sx={{ color: '#e2e8f0' }}>
                          Bloquear cierre con parada abierta
                        </Typography>
                      </Stack>

                      <Stack direction="row" spacing={1} alignItems="center">
                        <Switch
                          checked={operationForm.validation_rules.require_actual_quantity_on_close}
                          onChange={(event) =>
                            setOperationForm((current) =>
                              current
                                ? {
                                    ...current,
                                    validation_rules: {
                                      ...current.validation_rules,
                                      require_actual_quantity_on_close: event.target.checked,
                                      require_actual_meters_on_close: event.target.checked,
                                    },
                                  }
                                : current,
                            )
                          }
                        />
                        <Typography sx={{ color: '#e2e8f0' }}>
                          Exigir cantidad real al cierre
                        </Typography>
                      </Stack>

                      <Stack direction="row" spacing={1} alignItems="center">
                        <Switch
                          checked={operationForm.validation_rules.highlight_delay_vs_estimated}
                          onChange={(event) =>
                            setOperationForm((current) =>
                              current
                                ? {
                                    ...current,
                                    validation_rules: {
                                      ...current.validation_rules,
                                      highlight_delay_vs_estimated: event.target.checked,
                                    },
                                  }
                                : current,
                            )
                          }
                        />
                        <Typography sx={{ color: '#e2e8f0' }}>
                          Resaltar desviación frente a duración teórica
                        </Typography>
                      </Stack>
                    </Stack>
                  </Stack>
                </Paper>
              </Stack>
            )}
          </Stack>
        </Paper>
      )}

      <Dialog
        open={lineGroupDialogOpen}
        onClose={() => setLineGroupDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {lineGroupDialogMode === 'create' ? 'Nuevo grupo de líneas' : 'Editar grupo de líneas'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              size="small"
              label="Nombre"
              value={lineGroupForm.name}
              onChange={(event) => {
                const name = event.target.value;

                setLineGroupForm((current) => ({
                  ...current,
                  name,
                  code: current.code || normalizeCode(name),
                }));
              }}
              helperText="Ejemplo: EPDM, TPE, Envasado, Cocción."
              fullWidth
            />
            <TextField
              size="small"
              label="Código"
              value={lineGroupForm.code}
              onChange={(event) =>
                setLineGroupForm((current) => ({
                  ...current,
                  code: normalizeCode(event.target.value),
                }))
              }
              helperText="Código interno único del grupo."
              fullWidth
            />
            <TextField
              size="small"
              label="Descripción"
              value={lineGroupForm.description ?? ''}
              onChange={(event) =>
                setLineGroupForm((current) => ({
                  ...current,
                  description: event.target.value,
                }))
              }
              fullWidth
            />
            <Stack direction="row" spacing={1} alignItems="center">
              <Switch
                checked={lineGroupForm.is_active}
                onChange={(event) =>
                  setLineGroupForm((current) => ({
                    ...current,
                    is_active: event.target.checked,
                  }))
                }
              />
              <Typography>Activo</Typography>
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLineGroupDialogOpen(false)}>Cancelar</Button>
          <Button
            variant="contained"
            disabled={
              !lineGroupForm.code ||
              !lineGroupForm.name ||
              saveLineGroupMutation.isPending
            }
            onClick={() => saveLineGroupMutation.mutate(lineGroupForm)}
          >
            Guardar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={lineDialogOpen} onClose={() => setLineDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{lineDialogMode === 'create' ? 'Nueva línea' : 'Editar línea'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              size="small"
              label="Nombre"
              value={lineForm.name}
              onChange={(event) => {
                const name = event.target.value;
                setLineForm((current) => ({
                  ...current,
                  name,
                  code: current.code || normalizeCode(name),
                }));
              }}
              fullWidth
            />
            <TextField
              size="small"
              label="Código"
              value={lineForm.code}
              onChange={(event) =>
                setLineForm((current) => ({ ...current, code: normalizeCode(event.target.value) }))
              }
              fullWidth
            />
            <TextField
              select
              size="small"
              label="Grupo de líneas"
              value={lineForm.production_line_group_id ?? ''}
              onChange={(event) => {
                const productionLineGroupId = event.target.value || null;
                const selectedLineGroup = productionLineGroupId
                  ? lineGroupById.get(productionLineGroupId)
                  : null;

                setLineForm((current) => ({
                  ...current,
                  production_line_group_id: productionLineGroupId,
                  process_group: selectedLineGroup?.name ?? '',
                }));
              }}
              helperText="Selecciona el grupo operativo gestionado. Se mantiene compatibilidad interna con el nombre del grupo."
              fullWidth
            >
              <MenuItem value="">Sin grupo asignado</MenuItem>
              {lineGroups
                .filter(
                  (group) =>
                    group.is_active ||
                    group.id === lineForm.production_line_group_id,
                )
                .map((group) => (
                  <MenuItem key={group.id} value={group.id}>
                    {group.name}
                    {!group.is_active ? ' · Inactivo' : ''}
                  </MenuItem>
                ))}
            </TextField>
            <TextField
              size="small"
              label="Descripción"
              value={lineForm.description ?? ''}
              onChange={(event) =>
                setLineForm((current) => ({ ...current, description: event.target.value }))
              }
              fullWidth
            />
            <Stack direction="row" spacing={1} alignItems="center">
              <Switch
                checked={lineForm.is_active}
                onChange={(event) =>
                  setLineForm((current) => ({ ...current, is_active: event.target.checked }))
                }
              />
              <Typography>Activo</Typography>
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLineDialogOpen(false)}>Cancelar</Button>
          <Button
            variant="contained"
            disabled={!lineForm.code || !lineForm.name || saveLineMutation.isPending}
            onClick={() => saveLineMutation.mutate(lineForm)}
          >
            Guardar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={shiftDialogOpen} onClose={() => setShiftDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{shiftDialogMode === 'create' ? 'Nuevo turno' : 'Editar turno'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              size="small"
              label="Etiqueta"
              value={shiftForm.label}
              onChange={(event) => {
                const label = event.target.value;
                setShiftForm((current) => ({
                  ...current,
                  label,
                  code: current.code || normalizeCode(label).toLowerCase(),
                }));
              }}
              fullWidth
            />
            <TextField
              size="small"
              label="Código"
              value={shiftForm.code}
              onChange={(event) =>
                setShiftForm((current) => ({ ...current, code: event.target.value.trim().toLowerCase() }))
              }
              fullWidth
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
              <TextField
                size="small"
                type="time"
                label="Inicio"
                value={shiftForm.start_time}
                onChange={(event) =>
                  setShiftForm((current) => ({ ...current, start_time: event.target.value }))
                }
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
              <TextField
                size="small"
                type="time"
                label="Fin"
                value={shiftForm.end_time}
                onChange={(event) =>
                  setShiftForm((current) => ({ ...current, end_time: event.target.value }))
                }
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
            </Stack>
            <TextField
              size="small"
              type="number"
              label="Orden"
              value={shiftForm.display_order}
              onChange={(event) =>
                setShiftForm((current) => ({ ...current, display_order: Number(event.target.value) }))
              }
              fullWidth
            />
            <Stack direction="row" spacing={1} alignItems="center">
              <Switch
                checked={shiftForm.is_active}
                onChange={(event) =>
                  setShiftForm((current) => ({ ...current, is_active: event.target.checked }))
                }
              />
              <Typography>Activo</Typography>
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShiftDialogOpen(false)}>Cancelar</Button>
          <Button
            variant="contained"
            disabled={!shiftForm.code || !shiftForm.label || !shiftForm.start_time || !shiftForm.end_time || saveShiftMutation.isPending}
            onClick={() => saveShiftMutation.mutate(shiftForm)}
          >
            Guardar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={stopReasonDialogOpen} onClose={() => setStopReasonDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{stopReasonDialogMode === 'create' ? 'Nuevo motivo' : 'Editar motivo'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              size="small"
              label="Etiqueta"
              value={stopReasonForm.label}
              onChange={(event) => {
                const label = event.target.value;
                setStopReasonForm((current) => ({
                  ...current,
                  label,
                  code: current.code || normalizeCode(label).toLowerCase(),
                }));
              }}
              fullWidth
            />
            <TextField
              size="small"
              label="Código"
              value={stopReasonForm.code}
              onChange={(event) =>
                setStopReasonForm((current) => ({ ...current, code: event.target.value.trim().toLowerCase() }))
              }
              fullWidth
            />
            <TextField
              size="small"
              type="number"
              label="Orden"
              value={stopReasonForm.display_order}
              onChange={(event) =>
                setStopReasonForm((current) => ({ ...current, display_order: Number(event.target.value) }))
              }
              fullWidth
            />
            <Stack direction="row" spacing={1} alignItems="center">
              <Switch
                checked={stopReasonForm.is_active}
                onChange={(event) =>
                  setStopReasonForm((current) => ({ ...current, is_active: event.target.checked }))
                }
              />
              <Typography>Activo</Typography>
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStopReasonDialogOpen(false)}>Cancelar</Button>
          <Button
            variant="contained"
            disabled={!stopReasonForm.code || !stopReasonForm.label || saveStopReasonMutation.isPending}
            onClick={() => saveStopReasonMutation.mutate(stopReasonForm)}
          >
            Guardar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={qualityControlDialogOpen} onClose={() => setQualityControlDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{qualityControlDialogMode === 'create' ? 'Nuevo control' : 'Editar control'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              select
              size="small"
              label="Plantilla"
              value={qualityControlForm.template_id}
              onChange={(event) =>
                setQualityControlForm((current) => ({ ...current, template_id: event.target.value }))
              }
              fullWidth
            >
              {templates.map((template) => (
                <MenuItem key={template.id} value={template.id}>
                  {template.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              size="small"
              label="Etiqueta"
              value={qualityControlForm.label}
              onChange={(event) => {
                const label = event.target.value;
                setQualityControlForm((current) => ({
                  ...current,
                  label,
                  code: current.code || normalizeCode(label).toLowerCase(),
                }));
              }}
              fullWidth
            />
            <TextField
              size="small"
              label="Código"
              value={qualityControlForm.code}
              onChange={(event) =>
                setQualityControlForm((current) => ({ ...current, code: event.target.value.trim().toLowerCase() }))
              }
              fullWidth
            />
            <TextField
              size="small"
              label="Descripción"
              value={qualityControlForm.description ?? ''}
              onChange={(event) =>
                setQualityControlForm((current) => ({ ...current, description: event.target.value }))
              }
              fullWidth
            />
            <TextField
              size="small"
              type="number"
              label="Orden"
              value={qualityControlForm.display_order}
              onChange={(event) =>
                setQualityControlForm((current) => ({ ...current, display_order: Number(event.target.value) }))
              }
              fullWidth
            />
            <Stack direction="row" spacing={1} alignItems="center">
              <Switch
                checked={qualityControlForm.is_active}
                onChange={(event) =>
                  setQualityControlForm((current) => ({ ...current, is_active: event.target.checked }))
                }
              />
              <Typography>Activo</Typography>
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setQualityControlDialogOpen(false)}>Cancelar</Button>
          <Button
            variant="contained"
            disabled={!qualityControlForm.template_id || !qualityControlForm.code || !qualityControlForm.label || saveQualityControlMutation.isPending}
            onClick={() => saveQualityControlMutation.mutate(qualityControlForm)}
          >
            Guardar
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={plantUserDialogOpen} onClose={() => setPlantUserDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>
          {plantUserDialogMode === 'create' ? 'Crear usuario de planta' : 'Editar usuario de planta'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              size="small"
              label="Correo electrónico"
              value={plantUserForm.email}
              onChange={(event) =>
                setPlantUserForm((current) => ({ ...current, email: event.target.value }))
              }
              disabled={plantUserDialogMode === 'edit'}
              helperText={
                plantUserDialogMode === 'edit'
                  ? 'El correo no se modifica desde esta pantalla por ahora.'
                  : 'Este correo se usará para iniciar sesión en fábrica.'
              }
              fullWidth
            />

            {plantUserDialogMode === 'create' && (
              <TextField
                size="small"
                label="Contraseña temporal"
                type="password"
                value={plantUserForm.password}
                onChange={(event) =>
                  setPlantUserForm((current) => ({ ...current, password: event.target.value }))
                }
                helperText="Debe tener al menos 8 caracteres. El usuario podrá iniciar sesión con esta contraseña."
                fullWidth
              />
            )}

            <TextField
              size="small"
              label="Nombre"
              value={plantUserForm.full_name}
              onChange={(event) =>
                setPlantUserForm((current) => ({ ...current, full_name: event.target.value }))
              }
              fullWidth
            />

            <TextField
              size="small"
              select
              label="Rol"
              value={plantUserForm.role}
              onChange={(event) => {
                const nextRole = event.target.value as PlantUserAccessRole;

                setPlantUserForm((current) => ({
                  ...current,
                  role: nextRole,
                  permissions_override:
                    nextRole === 'factory_manager'
                      ? normalizePlantUserPermissionsOverride(current.permissions_override)
                      : {},
                }));
              }}
              fullWidth
            >
              <MenuItem value="factory_operator">Operario</MenuItem>
              <MenuItem value="factory_manager">Responsable de fábrica</MenuItem>
              <MenuItem value="admin">Administrador industrial</MenuItem>
            </TextField>

            <Stack direction="row" spacing={1} alignItems="center">
              <Switch
                checked={plantUserForm.is_active}
                onChange={(event) =>
                  setPlantUserForm((current) => ({ ...current, is_active: event.target.checked }))
                }
              />
              <Typography>Activo</Typography>
            </Stack>

            {plantUserDialogMode === 'edit' ? (
              <Paper
                elevation={0}
                sx={{
                  borderRadius: 3,
                  border: '1px solid rgba(148,163,184,0.16)',
                  bgcolor: 'rgba(2,6,23,0.22)',
                  p: 1.5,
                }}
              >
                <Stack spacing={1.5}>
                  <Stack spacing={0.3}>
                    <Typography sx={{ color: '#f8fafc', fontWeight: 900 }}>
                      Permisos
                    </Typography>
                    <Typography sx={{ color: '#94a3b8', fontSize: '0.84rem' }}>
                      El rol define una base de permisos. En v1, solo el responsable de fábrica admite ajustes personalizados.
                    </Typography>
                  </Stack>

                  {plantUserForm.role !== 'factory_manager' ? (
                    <Alert severity="info" variant="outlined">
                      {plantUserForm.role === 'admin'
                        ? 'El administrador industrial conserva siempre todos los permisos.'
                        : 'El operario queda limitado al puesto operario de fábrica.'}
                    </Alert>
                  ) : (
                    <Stack spacing={1}>
                      {configurablePlantUserPermissions.map((permission) => {
                        const enabled = resolvePlantUserPermissionEnabled(
                          plantUserForm.role,
                          plantUserForm.permissions_override,
                          permission.key,
                        );
                        const baseEnabled = factoryManagerBasePermissions.has(permission.key);

                        return (
                          <Paper
                            key={permission.key}
                            elevation={0}
                            sx={{
                              borderRadius: 2,
                              border: '1px solid rgba(148,163,184,0.12)',
                              bgcolor: 'rgba(15,23,42,0.38)',
                              px: 1.25,
                              py: 0.8,
                            }}
                          >
                            <Stack
                              direction="row"
                              spacing={1}
                              justifyContent="space-between"
                              alignItems="center"
                            >
                              <Stack spacing={0.1}>
                                <Typography sx={{ color: '#f8fafc', fontWeight: 800 }}>
                                  {permission.label}
                                </Typography>
                                <Typography sx={{ color: '#64748b', fontSize: '0.74rem' }}>
                                  {permission.group} · {baseEnabled ? 'Incluido por defecto' : 'No incluido por defecto'}
                                </Typography>
                              </Stack>

                              <Switch
                                checked={enabled}
                                onChange={(event) =>
                                  setPlantUserForm((current) => ({
                                    ...current,
                                    permissions_override: buildNextPlantUserPermissionsOverride(
                                      current.role,
                                      current.permissions_override,
                                      permission.key,
                                      event.target.checked,
                                    ),
                                  }))
                                }
                              />
                            </Stack>
                          </Paper>
                        );
                      })}
                    </Stack>
                  )}
                </Stack>
              </Paper>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPlantUserDialogOpen(false)}>Cancelar</Button>
          <Button
            variant="contained"
            disabled={
              savePlantUserMutation.isPending ||
              !plantUserForm.role ||
              !plantUserForm.email.trim() ||
              (plantUserDialogMode === 'create' && plantUserForm.password.length < 8)
            }
            onClick={() => savePlantUserMutation.mutate(plantUserForm)}
          >
            Guardar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={plantUserPasswordDialogOpen}
        onClose={() => setPlantUserPasswordDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Resetear contraseña</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Alert severity="warning" variant="outlined">
              Esta acción cambia la contraseña de inicio de sesión del usuario seleccionado.
            </Alert>

            <TextField
              size="small"
              label="Usuario"
              value={plantUserPasswordForm.email}
              disabled
              fullWidth
            />

            <TextField
              size="small"
              label="Nueva contraseña"
              type="password"
              value={plantUserPasswordForm.new_password}
              onChange={(event) =>
                setPlantUserPasswordForm((current) => ({
                  ...current,
                  new_password: event.target.value,
                }))
              }
              helperText="Debe tener al menos 8 caracteres."
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPlantUserPasswordDialogOpen(false)}>Cancelar</Button>
          <Button
            variant="contained"
            disabled={
              resetPlantUserPasswordMutation.isPending ||
              plantUserPasswordForm.new_password.length < 8
            }
            onClick={() =>
              resetPlantUserPasswordMutation.mutate({
                accessId: plantUserPasswordForm.access_id,
                payload: {
                  new_password: plantUserPasswordForm.new_password,
                },
              })
            }
          >
            Actualizar contraseña
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}